import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { procurementAPI, inventoryAPI, printAPI, settingsAPI, getErrorMessage } from '@/lib/api';
import GlobalFilters from '@/components/GlobalFilters';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExportColumnsDialog } from '@/components/ExportColumnsDialog';
import { toast } from 'sonner';
import { Plus, ShoppingBag, Trash2, Check, Edit, History, RotateCcw, XCircle, AlertCircle, Clock, User, Building, Search, Eye, Printer, Loader2, ArrowRightLeft, Wrench, FileSpreadsheet, Lock } from 'lucide-react';
import { TABLE_DENSITY } from '@/lib/tableDensity';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import MissingMasterDataDialog from '@/components/MissingMasterDataDialog';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ProcurementSupplierLedgerSummary } from '@/components/procurement/ProcurementSupplierLedgerSummary';
import { SupplierLedgerDialog } from '@/components/procurement/SupplierLedgerDialog';
import { safeRequest } from '@/lib/safeRequest';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { useLanguage } from '@/i18n';
import { hideResizeGuide, showResizeGuide } from '@/lib/resizeGuide';

const statusColors = {
  draft: 'bg-slate-100 text-slate-800',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  partially_received: 'bg-amber-100 text-amber-800',
  received: 'bg-green-100 text-green-800',
  closed: 'bg-slate-900 text-white',
  cancelled: 'bg-red-100 text-red-800'
};

const statusLabels = {
  draft: 'Rascunho',
  pending_approval: 'Pendente Aprovação',
  approved: 'Aprovada',
  partially_received: 'Parcialmente Recebida',
  received: 'Recebida',
  closed: 'Fechada',
  cancelled: 'Cancelada'
};

const paymentStatusColors = {
  not_payable: 'bg-slate-100 text-slate-700',
  unpaid: 'bg-amber-100 text-amber-800',
  partially_paid: 'bg-blue-100 text-blue-800',
  paid: 'bg-emerald-100 text-emerald-800',
};

const paymentStatusLabels = {
  not_payable: 'Não apta',
  unpaid: 'Pendente pagamento',
  partially_paid: 'Parcialmente paga',
  paid: 'Totalmente paga',
};

const fiscalRiskConfig = {
  alto: {
    label: 'ALTO',
    className: 'bg-red-100 text-red-800 border-red-300'
  },
  medio: {
    label: 'MÉDIO',
    className: 'bg-amber-100 text-amber-800 border-amber-300'
  },
  baixo: {
    label: 'BAIXO',
    className: 'bg-green-100 text-green-800 border-green-300'
  }
};

const supplierStatusColors = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-slate-100 text-slate-800'
};

// Status options for filter
const PO_STATUS_OPTIONS = ['draft', 'pending_approval', 'approved', 'partially_received', 'received', 'closed'];
const PROCUREMENT_TABS = ['pos', 'suppliers', 'deleted-suppliers', 'cancelled-pos'];
const INVENTORY_TABLE_HEAD_CLASS = TABLE_DENSITY.head;
const INVENTORY_TABLE_CELL_CLASS = TABLE_DENSITY.cell;
const PROCUREMENT_COLUMNS_STORAGE_KEY = 'procurement-po-columns-v1';
const PROCUREMENT_DEFAULT_COLUMN_WIDTHS = {
  selector: 44,
  po_number: 104,
  supplier: 154,
  fiscal_profile: 126,
  totals: 118,
  receipt: 120,
  financial: 144,
  risk: 84,
  status: 90,
  date: 106,
  actions: 156,
};

const resolveProcurementColumnWidths = () => {
  if (typeof window === 'undefined') return PROCUREMENT_DEFAULT_COLUMN_WIDTHS;
  try {
    const saved = window.sessionStorage.getItem(PROCUREMENT_COLUMNS_STORAGE_KEY);
    if (!saved) return PROCUREMENT_DEFAULT_COLUMN_WIDTHS;
    return { ...PROCUREMENT_DEFAULT_COLUMN_WIDTHS, ...JSON.parse(saved) };
  } catch (error) {
    return PROCUREMENT_DEFAULT_COLUMN_WIDTHS;
  }
};

const clampColumnWidth = (width) => Math.min(320, Math.max(60, Math.round(width)));
const INVENTORY_TABLE_ROW_CLASS = TABLE_DENSITY.row;

const PO_EXPORTABLE_COLUMNS = [
  { key: 'po_number', label: 'PO #' },
  { key: 'supplier_name', label: 'Fornecedor' },
  { key: 'total_amount', label: 'Valor Base' },
  { key: 'vat_total', label: 'IVA' },
  { key: 'total_with_vat', label: 'Valor c/ IVA' },
  { key: 'ordered_quantity_total', label: 'Qtd Encomendada' },
  { key: 'received_quantity_total', label: 'Qtd Recebida' },
  { key: 'pending_quantity_total', label: 'Qtd Pendente' },
  { key: 'receipt_completion_percent', label: '% Recepção' },
  { key: 'payable_amount_total', label: 'Apto Pagamento' },
  { key: 'paid_amount_total', label: 'Pago Fornecedor' },
  { key: 'supplier_balance_due', label: 'Saldo Fornecedor' },
  { key: 'payment_status', label: 'Estado Financeiro' },
  { key: 'fiscal_risk_level', label: 'Risco Fiscal' },
  { key: 'status', label: 'Estado' },
  { key: 'created_at', label: 'Criado Em' },
  { key: 'expected_delivery', label: 'Entrega Prevista' },
  { key: 'notes', label: 'Notas' },
];

const downloadBlobFile = (blob, filename) => {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

const getPOReceiptSummary = (po) => {
  const items = po?.items || [];
  const ordered = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const received = items.reduce((sum, item) => sum + Number(item.received_quantity || 0), 0);
  const pending = items.reduce((sum, item) => sum + Number(item.pending_quantity ?? Math.max(Number(item.quantity || 0) - Number(item.received_quantity || 0), 0)), 0);
  const completion = ordered > 0 ? Math.round((received / ordered) * 100) : 0;
  return { ordered, received, pending, completion };
};

const getPOFinancialSummary = (po) => ({
  payable: Number(po?.payable_amount_total || 0),
  paid: Number(po?.paid_amount_total || 0),
  balance: Number(po?.supplier_balance_due || 0),
  paymentStatus: po?.payment_status || 'not_payable',
  eligibleForPayment: Boolean(po?.eligible_for_payment),
});

const TIMELINE_PRESETS = [
  { value: 'all', label: 'Todo o período' },
  { value: 'today', label: 'Hoje' },
  { value: 'last_7_days', label: 'Últimos 7 dias' },
  { value: 'last_30_days', label: 'Últimos 30 dias' },
  { value: 'this_month', label: 'Este mês' },
  { value: 'previous_month', label: 'Mês anterior' },
  { value: 'this_year', label: 'Este ano' },
  { value: 'custom', label: 'Personalizado' },
];

const applyTimelinePreset = (preset) => {
  const now = new Date();
  const iso = (date) => date.toISOString().slice(0, 10);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  const startOfPreviousMonth = new Date(endOfPreviousMonth.getFullYear(), endOfPreviousMonth.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  switch (preset) {
    case 'today':
      return { start_date: iso(now), end_date: iso(now) };
    case 'last_7_days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 6);
      return { start_date: iso(start), end_date: iso(now) };
    }
    case 'last_30_days': {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { start_date: iso(start), end_date: iso(now) };
    }
    case 'this_month':
      return { start_date: iso(startOfMonth), end_date: iso(now) };
    case 'previous_month':
      return { start_date: iso(startOfPreviousMonth), end_date: iso(endOfPreviousMonth) };
    case 'this_year':
      return { start_date: iso(startOfYear), end_date: iso(now) };
    default:
      return { start_date: '', end_date: '' };
  }
};

export default function ProcurementPage() {
  const navigate = useNavigate();
  // URL Search Params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Global filters
  const { filters, setFilters } = useGlobalFilters({
    period: searchParams.get('period') || 'all',
    status: searchParams.get('status') || null,
  });
  
  const [suppliers, setSuppliers] = useState([]);
  const [supplierLedgerRows, setSupplierLedgerRows] = useState([]);
  const [supplierLedgerTotals, setSupplierLedgerTotals] = useState({ suppliers: 0, total_ordered: 0, total_received: 0, total_payable: 0, total_paid: 0, balance_due: 0 });
  const [deletedSuppliers, setDeletedSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [cancelledPOs, setCancelledPOs] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [poDialogOpen, setPODialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(PROCUREMENT_TABS.includes(initialTab) ? initialTab : 'pos');
  const procurementSyncSnapshotRef = useRef('');
  const hasInitialDataLoadedRef = useRef(false);
  const hasHydratedPurchaseOrdersRef = useRef(false);
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  const { t } = useLanguage();
  
  // Auth
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);
  const canEdit = hasRole(['admin', 'store_manager']);
  
  // Supplier form
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    tax_id: '',
    payment_terms: '',
    notes: ''
  });

  // Supplier Edit state
  const [editSupplierDialogOpen, setEditSupplierDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editSupplierForm, setEditSupplierForm] = useState(null);
  const [editSupplierJustification, setEditSupplierJustification] = useState('');
  const [confirmEditSupplierDialogOpen, setConfirmEditSupplierDialogOpen] = useState(false);
  
  // Supplier Delete state
  const [deleteSupplierDialogOpen, setDeleteSupplierDialogOpen] = useState(false);
  const [deleteSupplierReason, setDeleteSupplierReason] = useState('');
  
  // Supplier Audit state
  const [supplierAuditDialogOpen, setSupplierAuditDialogOpen] = useState(false);
  const [supplierAuditLogs, setSupplierAuditLogs] = useState([]);
  const [supplierLedgerDialogOpen, setSupplierLedgerDialogOpen] = useState(false);
  const [selectedSupplierLedger, setSelectedSupplierLedger] = useState(null);

  // PO Items state
  const [poItems, setPOItems] = useState([]);
  const [selectedSupplierForPO, setSelectedSupplierForPO] = useState(null);
  const [poSupplierChargesVat, setPOSupplierChargesVat] = useState(true);
  const [poSupplierIssuesFiscalDocument, setPOSupplierIssuesFiscalDocument] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [unitCost, setUnitCost] = useState('');
  const [poNotes, setPONotes] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  
  // PO Edit state
  const [editPODialogOpen, setEditPODialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [editPOSupplierId, setEditPOSupplierId] = useState('');
  const [editPOItems, setEditPOItems] = useState([]);
  const [editPOSupplierChargesVat, setEditPOSupplierChargesVat] = useState(true);
  const [editPOSupplierIssuesFiscalDocument, setEditPOSupplierIssuesFiscalDocument] = useState(true);
  const [editPONotes, setEditPONotes] = useState('');
  const [editPODelivery, setEditPODelivery] = useState('');
  const [editPOJustification, setEditPOJustification] = useState('');
  const [confirmEditPODialogOpen, setConfirmEditPODialogOpen] = useState(false);
  
  // PO Cancel state
  const [cancelPODialogOpen, setCancelPODialogOpen] = useState(false);
  const [cancelPOReason, setCancelPOReason] = useState('');
  
  // PO Audit state
  const [poAuditDialogOpen, setPOAuditDialogOpen] = useState(false);
  const [poAuditLogs, setPOAuditLogs] = useState([]);
  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [poExportDialogOpen, setPOExportDialogOpen] = useState(false);
  const [poExportColumns, setPOExportColumns] = useState(PO_EXPORTABLE_COLUMNS.map((column) => column.key));
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  const [bulkCancelPOReason, setBulkCancelPOReason] = useState('');
  
  // PO View state
  const [viewPODialogOpen, setViewPODialogOpen] = useState(false);
  const [printingPO, setPrintingPO] = useState(null);
  const [receivePODialogOpen, setReceivePODialogOpen] = useState(false);
  const [receivePOItems, setReceivePOItems] = useState([]);
  const [receivePONotes, setReceivePONotes] = useState('');
  const [receivingPOId, setReceivingPOId] = useState(null);
  const [closingPOId, setClosingPOId] = useState(null);
  const [paymentPODialogOpen, setPaymentPODialogOpen] = useState(false);
  const [registeringPaymentPOId, setRegisteringPaymentPOId] = useState(null);
  const [paymentPOForm, setPaymentPOForm] = useState({
    payments: [{ amount: '', payment_method_id: '', payment_account_id: '', reference: '', notes: '' }],
  });
  const [poColumnWidths, setPOColumnWidths] = useState(resolveProcurementColumnWidths);
  const [ledgerExporting, setLedgerExporting] = useState(false);
  const [timelineFilters, setTimelineFilters] = useState({ preset: 'all', start_date: '', end_date: '', event_type: 'all', status_filter: 'all' });
  const [poPrintValidationState, setPOPrintValidationState] = useState({
    open: false,
    warnings: [],
    blob: null,
    filename: '',
    po: null,
    copyType: 'Original'
  });
  const procurementResizeRef = useRef({ columnKey: null, startX: 0, startWidth: 0 });

  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const urlSearch = searchParams.get('q') || '';
    const normalizedTab = PROCUREMENT_TABS.includes(urlTab) ? urlTab : 'pos';
    const snapshot = `${normalizedTab}|${urlSearch}|${filters.period || 'all'}|${filters.status || ''}`;

    if (procurementSyncSnapshotRef.current === snapshot) return;
    procurementSyncSnapshotRef.current = snapshot;

    setActiveTab((prev) => (prev === normalizedTab ? prev : normalizedTab));
    setSearchQuery((prev) => (prev === urlSearch ? prev : urlSearch));
  }, [searchParams, filters.period, filters.status]);

  // Update URL when filters/tab/search change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.period && filters.period !== 'all') params.set('period', filters.period);
    if (filters.status) params.set('status', filters.status);
    params.set('tab', activeTab);
    if (searchQuery.trim()) params.set('q', searchQuery.trim());

    const snapshot = `${activeTab}|${searchQuery.trim()}|${filters.period || 'all'}|${filters.status || ''}`;
    procurementSyncSnapshotRef.current = snapshot;

    setSearchParams(params, { replace: true });
  }, [filters, activeTab, searchQuery, setSearchParams]);

  const loadPurchaseOrders = useCallback(async () => {
    try {
      const params = {
        period: filters.period || 'all',
      };
      if (filters.status) params.status_filter = filters.status;
      if (filters.custom_start) params.start_date = filters.custom_start;
      if (filters.custom_end) params.end_date = filters.custom_end;
      
      const response = await safeRequest(() => procurementAPI.listPOs(params), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'procurement.purchase_orders', onEvent: captureFetchTelemetry } });
      setPurchaseOrders(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar ordens de compra');
    }
  }, [captureFetchTelemetry, filters]);

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      resetFetchTelemetry();
      const [suppliersRes, productsRes, paymentMethodsRes, paymentAccountsRes, suppliersLedgerRes] = await Promise.all([
        safeRequest(() => procurementAPI.listSuppliers(), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'procurement.suppliers', onEvent: captureFetchTelemetry } }),
        safeRequest(() => inventoryAPI.listProducts(), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'inventory.products', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listPaymentMethods(false), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'settings.payment_methods', onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listActivePaymentAccounts(), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'settings.payment_accounts.active', onEvent: captureFetchTelemetry } }),
        safeRequest(() => procurementAPI.getSuppliersLedgerSummary(), { data: { rows: [], totals: {} } }, { telemetry: { module: 'procurement', endpoint: 'procurement.suppliers_ledger.summary', onEvent: captureFetchTelemetry } })
      ]);
      setSuppliers(suppliersRes.data || []);
      setProducts(productsRes.data || []);
      setPaymentMethods((paymentMethodsRes.data || []).filter((method) => method.is_active !== false && method.is_deleted !== true));
      setPaymentAccounts(paymentAccountsRes.data || []);
      setSupplierLedgerRows(suppliersLedgerRes.data?.rows || []);
      setSupplierLedgerTotals(suppliersLedgerRes.data?.totals || { suppliers: 0, total_ordered: 0, total_received: 0, total_payable: 0, total_paid: 0, balance_due: 0 });
      await loadPurchaseOrders();
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, loadPurchaseOrders, resetFetchTelemetry]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, [setFilters]);

  // Refresh function for GlobalFilters
  const handleRefresh = useCallback(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  const loadData = useCallback(async () => {
    await loadInitialData();
  }, [loadInitialData]);

  const loadDeletedSuppliers = useCallback(async () => {
    try {
      const response = await safeRequest(() => procurementAPI.listDeletedSuppliers(), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'procurement.deleted_suppliers', onEvent: captureFetchTelemetry } });
      setDeletedSuppliers(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar fornecedores eliminados');
    }
  }, [captureFetchTelemetry]);

  const loadCancelledPOs = useCallback(async () => {
    try {
      const response = await safeRequest(() => procurementAPI.listCancelledPOs(), { data: [] }, { telemetry: { module: 'procurement', endpoint: 'procurement.cancelled_purchase_orders', onEvent: captureFetchTelemetry } });
      setCancelledPOs(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar POs canceladas');
    }
  }, [captureFetchTelemetry]);

  useEffect(() => {
    if (activeTab === 'deleted-suppliers' && isAdmin) {
      loadDeletedSuppliers();
    }
    if (activeTab === 'cancelled-pos' && isAdmin) {
      loadCancelledPOs();
    }
  }, [activeTab, isAdmin, loadCancelledPOs, loadDeletedSuppliers]);

  useEffect(() => {
    if (hasInitialDataLoadedRef.current) return;
    hasInitialDataLoadedRef.current = true;
    void loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (loading || !hasInitialDataLoadedRef.current) {
      return;
    }
    if (!hasHydratedPurchaseOrdersRef.current) {
      hasHydratedPurchaseOrdersRef.current = true;
      return;
    }
    void loadPurchaseOrders();
  }, [filters, loadPurchaseOrders, loading]);

  useEffect(() => {
    const currentSupplier = suppliers.find((supplier) => supplier.id === selectedSupplierForPO && supplier.status !== 'inactive');
    if (!currentSupplier) return;
    setPOSupplierChargesVat(currentSupplier.charges_vat !== false);
    setPOSupplierIssuesFiscalDocument(currentSupplier.issues_fiscal_document !== false);
  }, [selectedSupplierForPO, suppliers]);

  useEffect(() => {
    setPOItems((current) => current.map((item) => ({
      ...item,
      supplier_charges_vat: poSupplierChargesVat,
      vat_rate: poSupplierChargesVat ? Number(item.vat_rate || 16) : 0,
    })));
  }, [poSupplierChargesVat]);

  useEffect(() => {
    const currentSupplier = suppliers.find((supplier) => supplier.id === editPOSupplierId && supplier.status !== 'inactive');
    if (!currentSupplier) return;
    setEditPOSupplierChargesVat(currentSupplier.charges_vat !== false);
    setEditPOSupplierIssuesFiscalDocument(currentSupplier.issues_fiscal_document !== false);
  }, [editPOSupplierId, suppliers]);

  useEffect(() => {
    setEditPOItems((current) => current.map((item) => ({
      ...item,
      supplier_charges_vat: editPOSupplierChargesVat,
      vat_rate: editPOSupplierChargesVat ? Number(item.vat_rate || 16) : 0,
    })));
  }, [editPOSupplierChargesVat]);

  // =============================================================================
  // SUPPLIER HANDLERS
  // =============================================================================

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      const cleanedForm = {
        name: supplierForm.name,
        contact_person: supplierForm.contact_person || null,
        phone: supplierForm.phone || null,
        email: supplierForm.email || null,
        address: supplierForm.address || null,
        city: supplierForm.city || null,
        tax_id: supplierForm.tax_id || null,
        payment_terms: supplierForm.payment_terms || null,
        notes: supplierForm.notes || null
      };
      await procurementAPI.createSupplier(cleanedForm);
      toast.success('Fornecedor criado com sucesso');
      setSupplierDialogOpen(false);
      setSupplierForm({ name: '', contact_person: '', phone: '', email: '', address: '', city: '', tax_id: '', payment_terms: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar fornecedor'));
    }
  };

  const handleOpenEditSupplier = (supplier) => {
    if (!canEdit) {
      toast.error('Sem permissão para editar fornecedores');
      return;
    }
    setSelectedSupplier(supplier);
    setEditSupplierForm({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      city: supplier.city || '',
      tax_id: supplier.tax_id || '',
      payment_terms: supplier.payment_terms || '',
      notes: supplier.notes || '',
      status: supplier.status || 'active'
    });
    setEditSupplierJustification('');
    setEditSupplierDialogOpen(true);
  };

  const handleSaveEditSupplier = () => {
    if (!editSupplierJustification || editSupplierJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    setConfirmEditSupplierDialogOpen(true);
  };

  const handleConfirmEditSupplier = async () => {
    try {
      const cleanedData = {
        name: editSupplierForm.name || undefined,
        contact_person: editSupplierForm.contact_person || null,
        phone: editSupplierForm.phone || null,
        email: editSupplierForm.email || null,
        address: editSupplierForm.address || null,
        city: editSupplierForm.city || null,
        tax_id: editSupplierForm.tax_id || null,
        payment_terms: editSupplierForm.payment_terms || null,
        notes: editSupplierForm.notes || null,
        status: editSupplierForm.status
      };
      
      await procurementAPI.editSupplier(selectedSupplier.id, cleanedData, editSupplierJustification.trim());
      toast.success('Fornecedor actualizado! Alteração registada no histórico.');
      setConfirmEditSupplierDialogOpen(false);
      setEditSupplierDialogOpen(false);
      setEditSupplierJustification('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar fornecedor'));
    }
  };

  const handleOpenDeleteSupplier = (supplier) => {
    if (!canEdit) {
      toast.error('Sem permissão para eliminar fornecedores');
      return;
    }
    setSelectedSupplier(supplier);
    setDeleteSupplierReason('');
    setDeleteSupplierDialogOpen(true);
  };

  const handleConfirmDeleteSupplier = async () => {
    if (!deleteSupplierReason || deleteSupplierReason.trim().length < 5) {
      toast.error('Motivo de eliminação obrigatório (mínimo 5 caracteres)');
      return;
    }
    
    try {
      await procurementAPI.deleteSupplier(selectedSupplier.id, deleteSupplierReason.trim());
      toast.success('Fornecedor eliminado com sucesso!');
      setDeleteSupplierDialogOpen(false);
      setDeleteSupplierReason('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao eliminar fornecedor'));
    }
  };

  const handleRestoreSupplier = async (supplier) => {
    try {
      await procurementAPI.restoreSupplier(supplier.id);
      toast.success('Fornecedor restaurado com sucesso!');
      loadDeletedSuppliers();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao restaurar fornecedor'));
    }
  };

  const handleViewSupplierAudit = async (supplier) => {
    setSelectedSupplier(supplier);
    try {
      const response = await procurementAPI.getSupplierAudit(supplier.id);
      setSupplierAuditLogs(response.data);
      setSupplierAuditDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleOpenSupplierLedger = async (supplier) => {
    try {
      const response = await procurementAPI.getSupplierLedgerDetail(supplier.id);
      setSelectedSupplierLedger(response.data || response);
      setTimelineFilters({ preset: 'all', start_date: '', end_date: '', event_type: 'all', status_filter: 'all' });
      setSupplierLedgerDialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar conta corrente do fornecedor'));
    }
  };

  const handleExportSuppliersLedger = async (supplierId = null) => {
    try {
      setLedgerExporting(true);
      const params = supplierId ? {
        supplier_id: supplierId,
        start_date: timelineFilters.start_date || undefined,
        end_date: timelineFilters.end_date || undefined,
        event_type: timelineFilters.event_type !== 'all' ? timelineFilters.event_type : undefined,
        status_filter: timelineFilters.status_filter !== 'all' ? timelineFilters.status_filter : undefined,
      } : {};
      const response = await procurementAPI.exportSuppliersLedgerXlsx(params);
      const fallbackName = supplierId ? `fornecedor_razao_${supplierId}.xlsx` : 'fornecedores_razao_consolidada.xlsx';
      downloadBlobFile(response.data, response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1] || fallbackName);
      toast.success(supplierId ? 'Conta corrente do fornecedor exportada.' : 'Conta corrente consolidada exportada.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao exportar conta corrente dos fornecedores'));
    } finally {
      setLedgerExporting(false);
    }
  };

  const handleTimelinePresetChange = (preset) => {
    const range = applyTimelinePreset(preset);
    setTimelineFilters((current) => ({
      ...current,
      preset,
      start_date: range.start_date,
      end_date: range.end_date,
    }));
  };

  // =============================================================================
  // PURCHASE ORDER HANDLERS
  // =============================================================================

  const addItemToPO = () => {
    if (!selectedProduct || !unitCost) {
      toast.error('Seleccione o produto e informe o custo');
      return;
    }
    const product = products.find(p => p.id === selectedProduct);
    const vatRate = poSupplierChargesVat ? Number(product?.vat_rate || getSupplierVatRate(true)) : 0;
    const unitCostValue = parseFloat(unitCost);
    const unitCostWithVat = unitCostValue * (1 + vatRate / 100);
    const vatAmount = (unitCostWithVat - unitCostValue) * quantity;

    setPOItems([...poItems, {
      product_id: product.id,
      product_name: product.name,
      quantity,
      unit_cost: unitCostValue,
      vat_rate: vatRate,
      supplier_charges_vat: poSupplierChargesVat,
      unit_cost_with_vat: parseFloat(unitCostWithVat.toFixed(2)),
      vat_amount: parseFloat(vatAmount.toFixed(2))
    }]);
    setSelectedProduct(null);
    setQuantity(1);
    setUnitCost('');
  };

  const removeItemFromPO = (index) => {
    setPOItems(poItems.filter((_, i) => i !== index));
  };

  const calculatePOTotal = () => {
    return poItems.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);
  };

  const calculatePOVATTotal = () => {
    return poItems.reduce((sum, item) => sum + getItemVatAmount(item), 0);
  };

  const calculatePOGrandTotal = () => {
    return calculatePOTotal() + calculatePOVATTotal();
  };

  const handleCreatePO = async () => {
    if (!selectedSupplierForPO || poItems.length === 0) {
      toast.error('Seleccione o fornecedor e adicione itens');
      return;
    }
    try {
      await procurementAPI.createPO({
        supplier_id: selectedSupplierForPO,
        items: poItems,
        supplier_charges_vat: poSupplierChargesVat,
        supplier_issues_fiscal_document: poSupplierIssuesFiscalDocument,
        supplier_vat_rate: getSupplierVatRate(poSupplierChargesVat),
        notes: poNotes || undefined,
        expected_delivery: expectedDelivery || undefined
      });
      toast.success('Ordem de compra criada com sucesso');
      setPODialogOpen(false);
      resetPOBuilder();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao criar ordem de compra'));
    }
  };

  const handleViewPO = (po) => {
    setSelectedPO(po);
    setViewPODialogOpen(true);
  };

  const refreshSelectedPO = async (poId = selectedPO?.id) => {
    if (!poId) return null;
    const response = await procurementAPI.getPO(poId);
    const updatedPO = response.data || response;
    setSelectedPO(updatedPO);
    return updatedPO;
  };

  const handleOpenEditPO = (po) => {
    if (po.status !== 'draft') {
      toast.error('Apenas ordens de compra em rascunho podem ser editadas');
      return;
    }
    if (!canEdit) {
      toast.error('Sem permissão para editar ordens de compra');
      return;
    }
    setSelectedPO(po);
    setEditPOSupplierId(po.supplier_id || '');
    setEditPOItems((po.items || []).map((item) => ({ ...item })));
    setEditPOSupplierChargesVat(po.supplier_charges_vat !== false);
    setEditPOSupplierIssuesFiscalDocument(po.supplier_issues_fiscal_document !== false);
    setEditPONotes(po.notes || '');
    setEditPODelivery(po.expected_delivery || '');
    setEditPOJustification('');
    setEditPODialogOpen(true);
  };

  const handleSaveEditPO = () => {
    if (!editPOSupplierId) {
      toast.error('Seleccione o fornecedor da PO.');
      return;
    }
    if (!editPOItems.length) {
      toast.error('Adicione pelo menos uma linha à PO.');
      return;
    }
    if (!editPOJustification || editPOJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    setConfirmEditPODialogOpen(true);
  };

  const handleConfirmEditPO = async () => {
    try {
      await procurementAPI.editPO(selectedPO.id, {
        supplier_id: editPOSupplierId,
        items: editPOItems,
        supplier_charges_vat: editPOSupplierChargesVat,
        supplier_issues_fiscal_document: editPOSupplierIssuesFiscalDocument,
        supplier_vat_rate: getSupplierVatRate(editPOSupplierChargesVat),
        notes: editPONotes || null,
        expected_delivery: editPODelivery || null
      }, editPOJustification.trim());
      
      toast.success('Ordem de compra actualizada! Alteração registada no histórico.');
      setConfirmEditPODialogOpen(false);
      setEditPODialogOpen(false);
      setEditPOJustification('');
      setEditPOItems([]);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao actualizar ordem de compra'));
    }
  };

  const handleApprovePO = async (poId) => {
    try {
      await procurementAPI.approvePO(poId);
      toast.success('Ordem de compra aprovada');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao aprovar ordem de compra'));
    }
  };

  const handleOpenReceivePO = async (po) => {
    try {
      const response = await procurementAPI.getPO(po.id);
      const latestPO = response.data || response;
      const summary = getPOReceiptSummary(latestPO);
      if (summary.pending <= 0) {
        toast.info('Esta ordem já foi totalmente recebida. Pode fechá-la operacionalmente.');
        setSelectedPO(latestPO);
        return;
      }

      setSelectedPO(latestPO);
      setReceivePOItems((latestPO.items || []).map((item) => ({
        product_id: item.product_id,
        product_name: item.product_name,
        ordered_quantity: Number(item.quantity || 0),
        received_quantity: Number(item.received_quantity || 0),
        pending_quantity: Number(item.pending_quantity ?? Math.max(Number(item.quantity || 0) - Number(item.received_quantity || 0), 0)),
        receive_now: Number(item.pending_quantity ?? Math.max(Number(item.quantity || 0) - Number(item.received_quantity || 0), 0)),
      })));
      setReceivePONotes('');
      setReceivePODialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao abrir recepção da ordem de compra'));
    }
  };

  const handleReceivePO = async () => {
    if (!selectedPO?.id) return;
    const payloadItems = receivePOItems
      .map((item) => ({ product_id: item.product_id, received_quantity: Number(item.receive_now || 0) }))
      .filter((item) => item.received_quantity > 0);

    if (!payloadItems.length) {
      toast.error('Indique pelo menos uma quantidade para recepção.');
      return;
    }

    try {
      setReceivingPOId(selectedPO.id);
      const response = await procurementAPI.receivePO(selectedPO.id, {
        items: payloadItems,
        notes: receivePONotes || undefined,
      });
      const payload = response.data || response;
      toast.success(payload.message || 'Recepção registada e stock actualizado');
      setReceivePODialogOpen(false);
      await loadData();
      await refreshSelectedPO(selectedPO.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao receber ordem de compra'));
    } finally {
      setReceivingPOId(null);
    }
  };

  const handleClosePO = async (poId) => {
    try {
      setClosingPOId(poId);
      const response = await procurementAPI.closePO(poId);
      const payload = response.data || response;
      toast.success(payload.message || 'Ordem de compra fechada');
      loadData();
      await refreshSelectedPO(poId);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao fechar ordem de compra'));
    } finally {
      setClosingPOId(null);
    }
  };

  const handleOpenPaymentPO = async (po) => {
    try {
      const latestPO = await refreshSelectedPO(po.id);
      const financial = getPOFinancialSummary(latestPO || po);
      if (!financial.eligibleForPayment) {
        toast.info('Esta ordem ainda não está apta para pagamento. O gatilho financeiro nasce da recepção real.');
        return;
      }

      setPaymentPOForm({
        payments: [{
          amount: String(financial.balance || financial.payable || ''),
          payment_method_id: paymentMethods[0]?.id || '',
          payment_account_id: '',
          reference: '',
          notes: '',
        }],
      });
      setPaymentPODialogOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao abrir pagamento do fornecedor'));
    }
  };

  const handleRegisterPOPayment = async () => {
    if (!selectedPO?.id) return;
    const normalizedLines = (paymentPOForm.payments || []).map((line) => ({
      ...line,
      amount: Number(line.amount || 0),
    })).filter((line) => line.amount > 0);
    if (!normalizedLines.length) {
      toast.error('Adicione pelo menos uma linha de pagamento.');
      return;
    }

    try {
      setRegisteringPaymentPOId(selectedPO.id);
      const response = await procurementAPI.registerPOPayment(selectedPO.id, {
        payments: normalizedLines,
      });
      const payload = response.data || response;
      toast.success(payload.message || 'Pagamento ao fornecedor registado');
      setPaymentPODialogOpen(false);
      await loadData();
      await refreshSelectedPO(selectedPO.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao registar pagamento ao fornecedor'));
    } finally {
      setRegisteringPaymentPOId(null);
    }
  };

  const handleOpenCancelPO = (po) => {
    if (!['draft', 'approved'].includes(po.status)) {
      toast.error('Apenas ordens de compra em rascunho ou aprovadas podem ser canceladas');
      return;
    }
    if (po.status === 'approved' && !isAdmin) {
      toast.error('Apenas Admin pode cancelar ordens de compra aprovadas');
      return;
    }
    setSelectedPO(po);
    setCancelPOReason('');
    setCancelPODialogOpen(true);
  };

  const handleConfirmCancelPO = async () => {
    if (!cancelPOReason || cancelPOReason.trim().length < 5) {
      toast.error('Motivo de cancelamento obrigatório (mínimo 5 caracteres)');
      return;
    }
    
    try {
      await procurementAPI.cancelPO(selectedPO.id, cancelPOReason.trim());
      toast.success('Ordem de compra cancelada');
      setCancelPODialogOpen(false);
      setCancelPOReason('');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao cancelar ordem de compra'));
    }
  };

  const handleViewPOAudit = async (po) => {
    setSelectedPO(po);
    try {
      const response = await procurementAPI.getPOAudit(po.id);
      setPOAuditLogs(response.data);
      setPOAuditDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const getSupplierMissingWarnings = (po) => {
    if (Array.isArray(po?.fiscal_risk_warnings)) {
      return po.fiscal_risk_warnings;
    }
    const snapshot = po?.supplier_snapshot || {};
    const warnings = [];
    if (!String(snapshot.tax_id || '').trim()) warnings.push('missing_supplier_tax_id');
    if (!String(snapshot.address || '').trim()) warnings.push('missing_supplier_address');
    if (!String(snapshot.city || '').trim()) warnings.push('missing_supplier_city');
    if (!String(snapshot.contact_person || '').trim()) warnings.push('missing_supplier_contact_person');
    return warnings;
  };

  const getFiscalRiskLevel = (po) => {
    const backendLevel = String(po?.fiscal_risk_level || '').toLowerCase();
    if (['alto', 'medio', 'baixo'].includes(backendLevel)) {
      return backendLevel;
    }
    const warnings = getSupplierMissingWarnings(po);
    if (warnings.includes('missing_supplier_tax_id') || warnings.includes('missing_supplier_nuit') || warnings.includes('missing_supplier_address')) {
      return 'alto';
    }
    if (warnings.includes('missing_supplier_city') || warnings.includes('missing_supplier_contact_person')) {
      return 'medio';
    }
    return 'baixo';
  };

  const handlePrintPO = async (po, copyType = 'Original') => {
    if (!po?.id) return;
    setPrintingPO(`${po.id}-${copyType}`);
    try {
      const result = await printAPI.renderDocumentPdf({
        doc_type: 'purchase_order',
        po_id: po.id,
        location_id: po.location_id,
        module_group: 'COMPRAS',
        copy_type: copyType
      }, { deferOnMissingData: true });

      if (result.success) {
        if (result.hasMissingData) {
          setPOPrintValidationState({
            open: true,
            warnings: result.warnings || [],
            blob: result.blob,
            filename: result.filename,
            po,
            copyType
          });
          return;
        }
        toast.success(`PO ${copyType} aberta para impressão`);
      } else {
        toast.error('Erro ao abrir PDF da PO', {
          description: result.error || 'Erro desconhecido'
        });
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao imprimir ordem de compra'));
    } finally {
      setPrintingPO(null);
    }
  };

  const handlePrintPOWithMissingData = (reason) => {
    if (poPrintValidationState.blob) {
      printAPI.openPdfBlob(poPrintValidationState.blob, poPrintValidationState.filename, 'preview');
      toast.warning('PO impressa com dados do fornecedor em falta', {
        description: reason ? `Motivo: ${reason}` : 'Complete o cadastro do fornecedor quando possível.'
      });
    }
    setPOPrintValidationState({ open: false, warnings: [], blob: null, filename: '', po: null, copyType: 'Original' });
  };

  const handleEditSupplierFromPOPrint = () => {
    setPOPrintValidationState({ open: false, warnings: [], blob: null, filename: '', po: null, copyType: 'Original' });
    window.open('/procurement', '_blank');
  };

  const normalizedSearch = searchQuery.toLowerCase();
  const normalizeSupplierSearchValue = (value) => String(value || '').toLowerCase();

  const filteredSuppliers = useMemo(() => suppliers.filter((supplier) => (
    normalizeSupplierSearchValue(supplier?.name).includes(normalizedSearch)
      || normalizeSupplierSearchValue(supplier?.contact_person).includes(normalizedSearch)
      || normalizeSupplierSearchValue(supplier?.email).includes(normalizedSearch)
      || String(supplier?.phone || '').includes(searchQuery)
      || normalizeSupplierSearchValue(supplier?.code).includes(normalizedSearch)
      || normalizeSupplierSearchValue(supplier?.supplier_code).includes(normalizedSearch)
  )), [suppliers, normalizedSearch, searchQuery]);

  const filteredSupplierTimeline = useMemo(() => {
    if (!selectedSupplierLedger?.timeline) return [];
    return selectedSupplierLedger.timeline.filter((event) => {
      const eventDate = (event.occurred_at || '').slice(0, 10);
      if (timelineFilters.event_type !== 'all' && event.event_type !== timelineFilters.event_type) return false;
      if (timelineFilters.status_filter !== 'all' && String(event.status) !== timelineFilters.status_filter) return false;
      if (timelineFilters.start_date && eventDate && eventDate < timelineFilters.start_date) return false;
      if (timelineFilters.end_date && eventDate && eventDate > timelineFilters.end_date) return false;
      return true;
    });
  }, [selectedSupplierLedger, timelineFilters]);

  const timelineStatusOptions = useMemo(() => Array.from(new Set((selectedSupplierLedger?.timeline || []).map((event) => String(event.status)).filter(Boolean))), [selectedSupplierLedger]);

  const filteredPurchaseOrders = useMemo(() => {
    if (!searchQuery.trim()) return purchaseOrders;
    return purchaseOrders.filter((po) => {
      const term = normalizedSearch;
      return [
        po.po_number,
        po.supplier_name,
        po.status,
        po.created_by_name,
      ].some((field) => String(field || '').toLowerCase().includes(term));
    });
  }, [purchaseOrders, searchQuery, normalizedSearch]);

  useEffect(() => {
    setSelectedPOIds((current) => current.filter((id) => filteredPurchaseOrders.some((po) => po.id === id)));
  }, [filteredPurchaseOrders]);

  const allVisiblePOsSelected = filteredPurchaseOrders.length > 0 && filteredPurchaseOrders.every((po) => selectedPOIds.includes(po.id));
  const hasProcurementData = Boolean(
    suppliers.length ||
    deletedSuppliers.length ||
    purchaseOrders.length ||
    cancelledPOs.length ||
    products.length
  );

  const togglePOSelection = (poId, checked) => {
    setSelectedPOIds((current) => (checked ? [...new Set([...current, poId])] : current.filter((id) => id !== poId)));
  };

  const toggleAllVisiblePOs = (checked) => {
    setSelectedPOIds(checked ? filteredPurchaseOrders.map((po) => po.id) : []);
  };

  const handleBulkPOAction = async (action, reason = null) => {
    if (!selectedPOIds.length) {
      toast.error('Seleccione POs para a ação em massa');
      return;
    }

    try {
      const response = await procurementAPI.bulkPOAction({ ids: selectedPOIds, action, reason });
      toast.success(response.data?.message || 'Ação em massa concluída');
      setSelectedPOIds([]);
      setBulkCancelDialogOpen(false);
      setBulkCancelPOReason('');
      await loadPurchaseOrders();
      if (activeTab === 'cancelled-pos' && isAdmin) {
        await loadCancelledPOs();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa das ordens de compra'));
    }
  };

  const togglePOExportColumn = (columnKey) => {
    setPOExportColumns((current) => current.includes(columnKey) ? current.filter((item) => item !== columnKey) : [...current, columnKey]);
  };

  const handleExportPOsXlsx = async () => {
    if (!selectedPOIds.length) {
      toast.error('Seleccione POs para exportar');
      return;
    }
    try {
      const response = await procurementAPI.exportPOsXlsx({ ids: selectedPOIds.join(','), columns: poExportColumns.join(',') });
      downloadBlobFile(response.data, `ordens_compra_${new Date().toISOString().slice(0, 10)}.xlsx`);
      setPOExportDialogOpen(false);
      toast.success('XLSX de ordens de compra gerado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao exportar POs'));
    }
  };

  // Get active suppliers for PO creation
  const activeSuppliers = suppliers.filter(s => s.status !== 'inactive');
  const selectedSupplierDetails = activeSuppliers.find((supplier) => supplier.id === selectedSupplierForPO) || null;
  const editSelectedSupplierDetails = activeSuppliers.find((supplier) => supplier.id === editPOSupplierId) || null;

  const getSupplierVatRate = (chargesVat) => (chargesVat ? 16 : 0);
  const getItemVatRate = (item) => {
    if (item?.supplier_charges_vat === false) return 0;
    const parsed = Number(item?.vat_rate);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
  };

  const getItemVatAmount = (item) => {
    const explicit = Number(item?.vat_amount);
    if (Number.isFinite(explicit) && explicit >= 0) return explicit;
    return (Number(item?.quantity || 0) * Number(item?.unit_cost || 0) * (getItemVatRate(item) / 100));
  };

  const getItemUnitCostWithVat = (item) => {
    const base = Number(item?.unit_cost || 0);
    const explicit = Number(item?.unit_cost_with_vat);
    if (Number.isFinite(explicit) && explicit >= base) return explicit;
    return base * (1 + (getItemVatRate(item) / 100));
  };

  const persistProcurementColumns = useCallback((nextWidths) => {
    setPOColumnWidths(nextWidths);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(PROCUREMENT_COLUMNS_STORAGE_KEY, JSON.stringify(nextWidths));
    }
  }, []);

  const startPOColumnResize = useCallback((columnKey, event) => {
    event.preventDefault();
    showResizeGuide(event.clientX);
    procurementResizeRef.current = {
      columnKey,
      startX: event.clientX,
      startWidth: poColumnWidths[columnKey] || PROCUREMENT_DEFAULT_COLUMN_WIDTHS[columnKey] || 120,
    };

    const handleMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - procurementResizeRef.current.startX;
      const nextWidth = clampColumnWidth(procurementResizeRef.current.startWidth + delta);
      showResizeGuide(moveEvent.clientX);
      persistProcurementColumns({
        ...poColumnWidths,
        [columnKey]: nextWidth,
      });
    };

    const handleMouseUp = () => {
      hideResizeGuide();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [persistProcurementColumns, poColumnWidths]);

  const resetPOBuilder = useCallback(() => {
    setSelectedSupplierForPO(null);
    setPOSupplierChargesVat(true);
    setPOSupplierIssuesFiscalDocument(true);
    setPOItems([]);
    setPONotes('');
    setExpectedDelivery('');
  }, []);

  const addPaymentSplitLine = useCallback(() => {
    setPaymentPOForm((current) => ({
      ...current,
      payments: [...(current.payments || []), { amount: '', payment_method_id: '', payment_account_id: '', reference: '', notes: '' }],
    }));
  }, []);

  const updatePaymentSplitLine = useCallback((index, field, value) => {
    setPaymentPOForm((current) => ({
      ...current,
      payments: (current.payments || []).map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const nextLine = { ...line, [field]: value };
        if (field === 'payment_method_id') {
          nextLine.payment_account_id = '';
        }
        return nextLine;
      }),
    }));
  }, []);

  const removePaymentSplitLine = useCallback((index) => {
    setPaymentPOForm((current) => ({
      ...current,
      payments: (current.payments || []).filter((_, lineIndex) => lineIndex !== index),
    }));
  }, []);

  return (
    <div className="p-4 space-y-4" data-testid="procurement-page">
      <ModulePageHero
        testId="procurement-page-hero"
        title={t('procurementPage.title')}
        description={t('procurementPage.description')}
        icon={ShoppingBag}
        badge="Compras"
        compact
        stats={[
          { label: 'PO visíveis', value: filteredPurchaseOrders.length, helper: 'no filtro actual' },
          { label: 'Fornecedores', value: filteredSuppliers.length, helper: 'base operacional' },
          { label: 'Seleccionados', value: selectedPOIds.length, helper: 'prontos para acções em lote' },
          { label: 'Pesquisa', value: searchQuery.trim() ? 'Activa' : 'Livre', helper: searchQuery.trim() || 'sem termo aplicado' },
        ]}
        actions={(
          <div className="flex flex-wrap items-center gap-2" data-testid="procurement-context-controls-row">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/stock-transfers')}
            data-testid="procurement-open-stock-transfers-button"
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            {t('procurementPage.stockTransfers')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/services')}
            data-testid="procurement-open-services-button"
          >
            <Wrench className="h-4 w-4 mr-2" />
            {t('procurementPage.services')}
          </Button>
          <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('procurementPage.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 pl-10 w-64 text-sm"
            data-testid="procurement-context-search-input"
          />
          </div>
          </div>
        )}
      />

      {/* Global Filters */}
      <GlobalFilters
        filters={['period', 'status']}
        statusOptions={PO_STATUS_OPTIONS}
        statusLabels={statusLabels}
        onChange={handleFilterChange}
        onRefresh={handleRefresh}
        loading={loading}
        defaultPeriod="all"
      />

      <ModuleStateBoundary
        loading={loading}
        hasData={hasProcurementData}
        partialIssues={partialIssues}
        loadingTestId="procurement-loading-state"
        partialIssuesTestId="procurement-partial-issues"
        emptyState={(
          <EmptyStatePanel
            title={t('procurementPage.emptyTitle')}
            description={t('procurementPage.emptyDescription')}
            ctaLabel={t('procurementPage.emptyCta')}
            ctaTo="/inventory"
            testId="procurement-empty-state"
          />
        )}
      >

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
          <TabsTrigger value="pos" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('procurementPage.tabs.purchaseOrders')}</TabsTrigger>
          <TabsTrigger value="suppliers" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">{t('procurementPage.tabs.suppliers')}</TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="deleted-suppliers" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                {t('procurementPage.tabs.deletedSuppliers')}
              </TabsTrigger>
              <TabsTrigger value="cancelled-pos" className="h-8 rounded-full border border-slate-200 bg-white px-3 text-[11px] shadow-none data-[state=active]:border-slate-900 data-[state=active]:bg-slate-900 data-[state=active]:text-white">
                {t('procurementPage.tabs.cancelledPOs')}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* =========== PURCHASE ORDERS TAB =========== */}
        <TabsContent value="pos" className="space-y-4">
          <div className="flex justify-end">
            {canEdit && (
              <Button size="sm" className="h-8 px-3 text-[11px]" onClick={() => { resetPOBuilder(); setPODialogOpen(true); }} data-testid="new-po-button">
                <Plus className="mr-2 h-4 w-4" />
                {t('procurementPage.newPO')}
              </Button>
            )}
          </div>

          <Card className="border-slate-200 shadow-sm" data-testid="purchase-orders-table">
            <CardHeader className="space-y-3 px-3 py-2.5">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <ShoppingBag className="h-5 w-5" />
                  {t('procurementPage.tabs.purchaseOrders')} ({filteredPurchaseOrders.length})
                  </CardTitle>
                  <p className="text-sm text-slate-500" data-testid="procurement-po-table-description">
                    Acompanhe ordens de compra, recepção, estado financeiro e risco fiscal num único quadro operacional.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="procurement-po-bulk-toolbar">
                  {selectedPOIds.length > 0 && (
                    <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700" data-testid="procurement-selection-badge">
                      {selectedPOIds.length} seleccionad{selectedPOIds.length > 1 ? 'os' : 'o'}
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" onClick={() => toggleAllVisiblePOs(!allVisiblePOsSelected)} data-testid="procurement-po-select-all-button">
                    {allVisiblePOsSelected ? t('procurementPage.clearSelection') : t('procurementPage.selectVisible')}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!selectedPOIds.length} onClick={() => handleBulkPOAction('approve')} data-testid="procurement-po-bulk-approve-button">
                    <Check className="mr-2 h-4 w-4" /> {t('procurementPage.approve')}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!selectedPOIds.length} onClick={() => handleBulkPOAction('receive')} data-testid="procurement-po-bulk-receive-button">
                    <RotateCcw className="mr-2 h-4 w-4" /> {t('procurementPage.receive')}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!selectedPOIds.length} onClick={() => setBulkCancelDialogOpen(true)} data-testid="procurement-po-bulk-cancel-button">
                    <XCircle className="mr-2 h-4 w-4" /> {t('actions.cancel')}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!selectedPOIds.length} onClick={() => setPOExportDialogOpen(true)} data-testid="procurement-po-export-xlsx-button">
                    <FileSpreadsheet className="mr-2 h-4 w-4" /> {t('procurementPage.configurableXlsx')}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {loading ? (
                <p className="text-center text-muted-foreground py-4">{t('procurementPage.loadingPOs')}</p>
              ) : filteredPurchaseOrders.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{t('procurementPage.noPOs')}</p>
              ) : (
                <div className="max-h-[72vh] overflow-y-auto" data-testid="procurement-po-table-wrapper">
                  <div className="overflow-x-hidden">
                  <table className="w-full table-fixed text-[9px] leading-tight" data-testid="procurement-po-table">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
                      <tr>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.selector, minWidth: poColumnWidths.selector }}>
                          <Checkbox checked={allVisiblePOsSelected} onCheckedChange={(checked) => toggleAllVisiblePOs(Boolean(checked))} data-testid="procurement-po-select-all-checkbox" />
                        </th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.po_number, minWidth: poColumnWidths.po_number }}>PO #<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('po_number', event)} data-testid="procurement-po-column-resize-po-number"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.supplier, minWidth: poColumnWidths.supplier }}>Fornecedor<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('supplier', event)} data-testid="procurement-po-column-resize-supplier"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.fiscal_profile, minWidth: poColumnWidths.fiscal_profile }}>Perfil fiscal<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('fiscal_profile', event)} data-testid="procurement-po-column-resize-fiscal-profile"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`} style={{ width: poColumnWidths.totals, minWidth: poColumnWidths.totals }}>Valor<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('totals', event)} data-testid="procurement-po-column-resize-totals"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.receipt, minWidth: poColumnWidths.receipt }}>Recepção<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('receipt', event)} data-testid="procurement-po-column-resize-receipt"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.financial, minWidth: poColumnWidths.financial }}>Financeiro<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('financial', event)} data-testid="procurement-po-column-resize-financial"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} data-testid="po-list-risk-header" style={{ width: poColumnWidths.risk, minWidth: poColumnWidths.risk }}>Risco Fiscal<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('risk', event)} data-testid="procurement-po-column-resize-risk"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.status, minWidth: poColumnWidths.status }}>Estado<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('status', event)} data-testid="procurement-po-column-resize-status"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={INVENTORY_TABLE_HEAD_CLASS} style={{ width: poColumnWidths.date, minWidth: poColumnWidths.date }}>Data<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('date', event)} data-testid="procurement-po-column-resize-date"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                        <th className={`${INVENTORY_TABLE_HEAD_CLASS} text-center`} style={{ width: poColumnWidths.actions, minWidth: poColumnWidths.actions }}>Acções<button type="button" className="ml-2 align-middle text-slate-300 hover:text-slate-600" onMouseDown={(event) => startPOColumnResize('actions', event)} data-testid="procurement-po-column-resize-actions"><span className="mx-auto block h-4 w-px bg-slate-300" /></button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPurchaseOrders.map((po) => (
                        <tr key={po.id} className={cn(INVENTORY_TABLE_ROW_CLASS, selectedPOIds.includes(po.id) && 'bg-indigo-50/35')} data-testid={`procurement-po-row-${po.id}`}>
                          {(() => {
                            const riskLevel = getFiscalRiskLevel(po);
                            const risk = fiscalRiskConfig[riskLevel] || fiscalRiskConfig.baixo;
                            return (
                              <>
                          {(() => {
                            const receipt = getPOReceiptSummary(po);
                            const financial = getPOFinancialSummary(po);
                            return (
                              <>
                          <td className={INVENTORY_TABLE_CELL_CLASS} style={{ width: poColumnWidths.selector, minWidth: poColumnWidths.selector }}>
                            <Checkbox checked={selectedPOIds.includes(po.id)} onCheckedChange={(checked) => togglePOSelection(po.id, Boolean(checked))} data-testid={`procurement-po-select-${po.id}`} />
                          </td>
                          <td className={INVENTORY_TABLE_CELL_CLASS} style={{ width: poColumnWidths.po_number, minWidth: poColumnWidths.po_number }}>
                            <div className="space-y-0.5">
                              <p className="truncate font-mono text-[11px] font-semibold text-slate-900">{po.po_number}</p>
                              <p className="text-[10px] text-slate-500">{(po.items || []).length} linha(s)</p>
                            </div>
                          </td>
                          <td className={INVENTORY_TABLE_CELL_CLASS} style={{ width: poColumnWidths.supplier, minWidth: poColumnWidths.supplier }}>
                            <div className="space-y-0.5">
                              <p className="truncate font-medium text-slate-900">{po.supplier_name}</p>
                              <p className="text-[10px] text-slate-500">NUIT: {po.supplier_snapshot?.tax_id || po.supplier_tax_id || 'N/A'}</p>
                            </div>
                          </td>
                          <td className={INVENTORY_TABLE_CELL_CLASS} style={{ width: poColumnWidths.fiscal_profile, minWidth: poColumnWidths.fiscal_profile }}>
                            <div className="space-y-1 text-xs text-slate-600" data-testid={`po-fiscal-profile-${po.id}`}>
                              <div className="font-medium text-slate-900">{po.supplier_charges_vat === false ? 'Sem IVA' : 'Com IVA'}</div>
                              <div>{po.supplier_issues_fiscal_document === false ? 'Sem doc. fiscal' : 'Doc. fiscal formal'}</div>
                            </div>
                          </td>
                          <td className={`${INVENTORY_TABLE_CELL_CLASS} text-right font-medium`} style={{ width: poColumnWidths.totals, minWidth: poColumnWidths.totals }}>
                            <div className="space-y-0.5 text-right">
                              <p className="text-slate-900">{formatCurrency(po.total_amount)}</p>
                              <p className="text-[10px] text-slate-500">IVA {formatCurrency(po.total_with_vat || 0)}</p>
                            </div>
                          </td>
                          <td className={`${INVENTORY_TABLE_CELL_CLASS} text-sm`} data-testid={`po-receipt-summary-${po.id}`} style={{ width: poColumnWidths.receipt, minWidth: poColumnWidths.receipt }}>
                            <div className="space-y-0.5">
                              <div className="font-medium text-slate-800">{receipt.received}/{receipt.ordered} recebidos</div>
                              <div className="text-[10px] text-slate-500">{receipt.pending} pend. · {receipt.completion}%</div>
                            </div>
                          </td>
                          <td className={`${INVENTORY_TABLE_CELL_CLASS} text-sm`} data-testid={`po-financial-summary-${po.id}`} style={{ width: poColumnWidths.financial, minWidth: poColumnWidths.financial }}>
                            <div className="space-y-0.5">
                              <Badge variant="outline" className={paymentStatusColors[financial.paymentStatus]}>
                                {paymentStatusLabels[financial.paymentStatus]}
                              </Badge>
                              <div className="text-[10px] text-slate-600">Pago {formatCurrency(financial.paid)} · Saldo {formatCurrency(financial.balance)}</div>
                            </div>
                          </td>
                          <td className={INVENTORY_TABLE_CELL_CLASS} data-testid={`po-list-risk-${po.id}`} style={{ width: poColumnWidths.risk, minWidth: poColumnWidths.risk }}>
                            <Badge className={risk.className} variant="outline" data-testid={`po-list-risk-badge-${po.id}`}>
                              {risk.label}
                            </Badge>
                          </td>
                          <td className={INVENTORY_TABLE_CELL_CLASS} style={{ width: poColumnWidths.status, minWidth: poColumnWidths.status }}>
                            <Badge className={statusColors[po.status]} variant="outline">
                              {statusLabels[po.status] || po.status}
                            </Badge>
                          </td>
                          <td className={INVENTORY_TABLE_CELL_CLASS} style={{ width: poColumnWidths.date, minWidth: poColumnWidths.date }}>
                            <div className="space-y-0.5 text-[10px]">
                              <p>{formatDateTime(po.created_at)}</p>
                              <p className="text-[10px] text-slate-500">Entrega: {po.expected_delivery || 'A definir'}</p>
                            </div>
                          </td>
                          <td className={`${INVENTORY_TABLE_CELL_CLASS} text-center`} style={{ width: poColumnWidths.actions, minWidth: poColumnWidths.actions }}>
                            <div className="flex flex-nowrap justify-center gap-1 overflow-x-auto">
                              {/* View */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewPO(po)}
                                title="Ver Detalhes"
                                data-testid={`view-po-${po.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handlePrintPO(po, 'Original')}
                                title="Imprimir PO"
                                data-testid={`print-po-original-${po.id}`}
                              >
                                {printingPO === `${po.id}-Original` ? (
                                  <Loader2 className="h-4 w-4 text-indigo-600 animate-spin" />
                                ) : (
                                  <Printer className="h-4 w-4 text-indigo-600" />
                                )}
                              </Button>
                              
                              {/* Edit - only Draft */}
                              {po.status === 'draft' && canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenEditPO(po)}
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4 text-amber-600" />
                                </Button>
                              )}
                              
                              {/* Approve - only Draft */}
                              {po.status === 'draft' && canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleApprovePO(po.id)}
                                  title="Aprovar"
                                  data-testid={`approve-po-${po.id}`}
                                >
                                  <Check className="h-4 w-4 text-blue-600" />
                                </Button>
                              )}
                              
                              {/* Receive - Approved or Partially Received */}
                              {['approved', 'partially_received'].includes(po.status) && canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenReceivePO(po)}
                                  title="Receber"
                                  data-testid={`receive-po-${po.id}`}
                                >
                                  <Check className="h-4 w-4 text-green-600" />
                                </Button>
                              )}

                              {po.status === 'received' && canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleClosePO(po.id)}
                                  title="Fechar PO"
                                  data-testid={`close-po-${po.id}`}
                                  disabled={closingPOId === po.id}
                                >
                                  {closingPOId === po.id ? <Loader2 className="h-4 w-4 animate-spin text-slate-700" /> : <Lock className="h-4 w-4 text-slate-700" />}
                                </Button>
                              )}

                              {financial.eligibleForPayment && financial.balance > 0 && canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenPaymentPO(po)}
                                  title="Registar pagamento fornecedor"
                                  data-testid={`pay-po-${po.id}`}
                                >
                                  <ArrowRightLeft className="h-4 w-4 text-emerald-700" />
                                </Button>
                              )}
                              
                              {/* Cancel - Draft or Approved */}
                              {['draft', 'approved', 'partially_received'].includes(po.status) && canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenCancelPO(po)}
                                  title="Cancelar"
                                >
                                  <XCircle className="h-4 w-4 text-red-600" />
                                </Button>
                              )}
                              
                              {/* Audit History */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewPOAudit(po)}
                                title="Ver Histórico"
                              >
                                <History className="h-4 w-4 text-indigo-600" />
                              </Button>
                            </div>
                          </td>
                              </>
                            );
                          })()}
                              </>
                            );
                          })()}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* =========== SUPPLIERS TAB =========== */}
        <TabsContent value="suppliers" className="space-y-4">
          <ProcurementSupplierLedgerSummary
            totals={supplierLedgerTotals}
            formatCurrency={formatCurrency}
            ledgerExporting={ledgerExporting}
            onExport={() => handleExportSuppliersLedger()}
            canEdit={canEdit}
            onAddSupplier={() => setSupplierDialogOpen(true)}
            addSupplierLabel={t('procurementPage.addSupplier')}
          />

          <Card data-testid="suppliers-table">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                {t('procurementPage.tabs.suppliers')} ({filteredSuppliers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center text-muted-foreground py-4">{t('procurementPage.loadingSuppliers')}</p>
              ) : filteredSuppliers.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  {searchQuery ? t('procurementPage.noSupplierFound') : t('procurementPage.noSuppliers')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Nome</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Código</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contacto</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Telefone</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Conta Corrente</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Estado</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Acções</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSuppliers.map((supplier) => {
                        const ledger = supplierLedgerRows.find((row) => row.supplier_id === supplier.id) || { total_ordered: 0, total_received: 0, total_payable: 0, total_paid: 0, balance_due: 0, payment_status: 'not_payable', po_count: 0 };
                        return (
                        <tr key={supplier.id} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <p className="font-medium">{supplier.name}</p>
                            {supplier.tax_id && (
                              <p className="text-xs text-muted-foreground">NUIT: {supplier.tax_id}</p>
                            )}
                          </td>
                          		<td className="py-3 px-4 text-sm" data-testid={`supplier-code-${supplier.id}`}>
                            {supplier.code || supplier.supplier_code || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm">{supplier.contact_person || '-'}</td>
                          <td className="py-3 px-4 text-sm">{supplier.phone || '-'}</td>
                          <td className="py-3 px-4 text-sm">{supplier.email || '-'}</td>
                          <td className="py-3 px-4 text-sm" data-testid={`supplier-ledger-summary-${supplier.id}`}>
                            <div className="space-y-1">
                              <Badge variant="outline" className={paymentStatusColors[ledger.payment_status] || paymentStatusColors.not_payable}>{paymentStatusLabels[ledger.payment_status] || paymentStatusLabels.not_payable}</Badge>
                              <div className="text-xs text-slate-600">Encomendado: {formatCurrency(ledger.total_ordered || 0)}</div>
                              <div className="text-xs text-slate-600">Recebido: {formatCurrency(ledger.total_received || 0)} · Apto: {formatCurrency(ledger.total_payable || 0)}</div>
                              <div className="text-xs text-slate-600">Pago: {formatCurrency(ledger.total_paid || 0)} · Saldo: {formatCurrency(ledger.balance_due || 0)}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={supplierStatusColors[supplier.status || 'active']} variant="outline">
                              {supplier.status === 'inactive' ? 'Inactivo' : 'Activo'}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center gap-1">
                              {/* Edit */}
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenEditSupplier(supplier)}
                                  title="Editar Fornecedor"
                                >
                                  <Edit className="h-4 w-4 text-amber-600" />
                                </Button>
                              )}
                              
                              {/* Delete */}
                              {canEdit && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenDeleteSupplier(supplier)}
                                  title="Eliminar Fornecedor"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              )}
                              
                              {/* Audit History */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewSupplierAudit(supplier)}
                                title="Ver Histórico"
                              >
                                <History className="h-4 w-4 text-indigo-600" />
                              </Button>

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenSupplierLedger(supplier)}
                                title="Ver Conta Corrente"
                                data-testid={`supplier-ledger-button-${supplier.id}`}
                              >
                                <Eye className="h-4 w-4 text-emerald-700" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* =========== DELETED SUPPLIERS TAB (Admin) =========== */}
        {isAdmin && (
          <TabsContent value="deleted-suppliers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="h-5 w-5" />
                  {t('procurementPage.deletedSuppliersTitle', { count: deletedSuppliers.length })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deletedSuppliers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('procurementPage.noDeletedSuppliers')}</p>
                ) : (
                  <div className="space-y-3">
                    {deletedSuppliers.map((supplier) => (
                      <div
                        key={supplier.id}
                        className="flex items-center justify-between border rounded-md p-4 bg-red-50 border-red-200"
                      >
                        <div>
                          <p className="font-medium">{supplier.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {t('procurementPage.deletedAtBy', { date: formatDateTime(supplier.deleted_at), name: supplier.deleted_by_name })}
                          </p>
                          <p className="text-sm text-red-600">{t('procurementPage.reasonLabel')}: {supplier.delete_reason}</p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => handleRestoreSupplier(supplier)}
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          {t('actions.restore')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* =========== CANCELLED POs TAB (Admin) =========== */}
        {isAdmin && (
          <TabsContent value="cancelled-pos" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  {t('procurementPage.cancelledPOsTitle', { count: cancelledPOs.length })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cancelledPOs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('procurementPage.noCancelledPOs')}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="border-b">
                        <tr>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">PO #</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('procurementPage.tabs.suppliers')}</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t('labels.amount')}</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('procurementPage.cancelledAt')}</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('procurementPage.cancelledBy')}</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t('procurementPage.reasonLabel')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancelledPOs.map((po) => (
                          <tr key={po.id} className="border-b last:border-0 bg-red-50">
                            <td className="py-3 px-4 font-mono text-sm">{po.po_number}</td>
                            <td className="py-3 px-4">{po.supplier_name}</td>
                            <td className="py-3 px-4 text-right font-medium">{formatCurrency(po.total_amount)}</td>
                            <td className="py-3 px-4 text-sm">{formatDateTime(po.cancelled_at)}</td>
                            <td className="py-3 px-4 text-sm">{po.cancelled_by_name}</td>
                            <td className="py-3 px-4 text-sm text-red-600">{po.cancel_reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
      </ModuleStateBoundary>

      <ExportColumnsDialog
        open={poExportDialogOpen}
        onOpenChange={setPOExportDialogOpen}
        title={t('procurementPage.exportDialogTitle')}
        description={t('procurementPage.exportDialogDescription')}
        columns={PO_EXPORTABLE_COLUMNS}
        selectedColumns={poExportColumns}
        onToggleColumn={togglePOExportColumn}
        onSelectAll={(checked) => setPOExportColumns(checked ? PO_EXPORTABLE_COLUMNS.map((column) => column.key) : [])}
        onConfirm={handleExportPOsXlsx}
        dataTestId="procurement-po-export-columns-dialog"
      />

      <AlertDialog open={bulkCancelDialogOpen} onOpenChange={setBulkCancelDialogOpen}>
        <AlertDialogContent data-testid="procurement-po-bulk-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('procurementPage.bulkCancelTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('procurementPage.bulkCancelDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="procurement-po-bulk-cancel-reason">{t('procurementPage.reasonLabel')} *</Label>
            <Textarea
              id="procurement-po-bulk-cancel-reason"
              value={bulkCancelPOReason}
              onChange={(event) => setBulkCancelPOReason(event.target.value)}
              placeholder={t('procurementPage.bulkCancelPlaceholder')}
              data-testid="procurement-po-bulk-cancel-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="procurement-po-bulk-cancel-close-button">{t('actions.close')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleBulkPOAction('cancel', bulkCancelPOReason)}
              data-testid="procurement-po-bulk-cancel-confirm-button"
            >
              {t('procurementPage.confirmCancellation')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* =========== CREATE SUPPLIER DIALOG =========== */}
      <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('procurementPage.addSupplierTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSupplier} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('procurementPage.supplierName')} *</Label>
              <Input
                id="name"
                value={supplierForm.name}
                onChange={(e) => setSupplierForm({...supplierForm, name: e.target.value})}
                required
                data-testid="supplier-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_person">{t('procurementPage.contactPerson')}</Label>
                <Input
                  id="contact_person"
                  value={supplierForm.contact_person}
                  onChange={(e) => setSupplierForm({...supplierForm, contact_person: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_id">{t('procurementPage.taxId')}</Label>
                <Input
                  id="tax_id"
                  value={supplierForm.tax_id}
                  onChange={(e) => setSupplierForm({...supplierForm, tax_id: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{t('labels.phone')}</Label>
                <Input
                  id="phone"
                  value={supplierForm.phone}
                  onChange={(e) => setSupplierForm({...supplierForm, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={supplierForm.email}
                  onChange={(e) => setSupplierForm({...supplierForm, email: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address">{t('procurementPage.address')}</Label>
                <Input
                  id="address"
                  value={supplierForm.address}
                  onChange={(e) => setSupplierForm({...supplierForm, address: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">{t('procurementPage.city')}</Label>
                <Input
                  id="city"
                  value={supplierForm.city}
                  onChange={(e) => setSupplierForm({...supplierForm, city: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_terms">{t('procurementPage.paymentTerms')}</Label>
              <Input
                id="payment_terms"
                value={supplierForm.payment_terms}
                onChange={(e) => setSupplierForm({...supplierForm, payment_terms: e.target.value})}
                placeholder={t('procurementPage.paymentTermsPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={supplierForm.notes}
                onChange={(e) => setSupplierForm({...supplierForm, notes: e.target.value})}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit">{t('procurementPage.addSupplier')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* =========== EDIT SUPPLIER DIALOG =========== */}
      <Dialog open={editSupplierDialogOpen} onOpenChange={setEditSupplierDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              Editar Fornecedor - {selectedSupplier?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editSupplierForm && (
            <div className="space-y-4">
              {/* Warning banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Edição Auditada</p>
                    <p className="text-sm text-amber-700">
                      Todas as alterações ficam registadas no histórico de auditoria.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={editSupplierForm.name}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select 
                    value={editSupplierForm.status} 
                    onValueChange={(v) => setEditSupplierForm({...editSupplierForm, status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activo</SelectItem>
                      <SelectItem value="inactive">Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pessoa de Contacto</Label>
                  <Input
                    value={editSupplierForm.contact_person}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, contact_person: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>NUIT</Label>
                  <Input
                    value={editSupplierForm.tax_id}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, tax_id: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={editSupplierForm.phone}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, phone: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editSupplierForm.email}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, email: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Morada</Label>
                  <Input
                    value={editSupplierForm.address}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, address: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade</Label>
                  <Input
                    value={editSupplierForm.city}
                    onChange={(e) => setEditSupplierForm({...editSupplierForm, city: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Termos de Pagamento</Label>
                <Input
                  value={editSupplierForm.payment_terms}
                  onChange={(e) => setEditSupplierForm({...editSupplierForm, payment_terms: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={editSupplierForm.notes}
                  onChange={(e) => setEditSupplierForm({...editSupplierForm, notes: e.target.value})}
                  rows={2}
                />
              </div>
              
              {/* Mandatory Justification */}
              <div className="space-y-2 border-t pt-4">
                <Label className="text-amber-700 font-medium">Justificação da Alteração *</Label>
                <Textarea
                  value={editSupplierJustification}
                  onChange={(e) => setEditSupplierJustification(e.target.value)}
                  placeholder="Descreva o motivo desta alteração (obrigatório)..."
                  rows={2}
                  className="border-amber-300 focus:border-amber-500"
                />
                <p className="text-xs text-muted-foreground">
                  Mínimo 5 caracteres. Esta justificação será registada no histórico.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditSupplierDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveEditSupplier}
                  disabled={!editSupplierJustification || editSupplierJustification.trim().length < 5}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Guardar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* =========== CONFIRM EDIT SUPPLIER DIALOG =========== */}
      <AlertDialog open={confirmEditSupplierDialogOpen} onOpenChange={setConfirmEditSupplierDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Confirmar Alteração
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Esta alteração ficará registada no histórico de auditoria.</p>
              <p className="mt-2">
                <strong>Justificação:</strong> &ldquo;{editSupplierJustification}&rdquo;
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEditSupplier}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* =========== DELETE SUPPLIER DIALOG =========== */}
      <AlertDialog open={deleteSupplierDialogOpen} onOpenChange={setDeleteSupplierDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Eliminar Fornecedor
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Tem certeza que deseja eliminar o fornecedor <strong>{selectedSupplier?.name}</strong>?
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-red-800">
                  O fornecedor será marcado como eliminado mas poderá ser restaurado pelo Admin.
                </p>
              </div>
              
              <div className="space-y-2 mt-4">
                <Label className="text-red-700 font-medium">Motivo da Eliminação *</Label>
                <Textarea
                  value={deleteSupplierReason}
                  onChange={(e) => setDeleteSupplierReason(e.target.value)}
                  placeholder="Descreva o motivo da eliminação (obrigatório)..."
                  rows={2}
                  className="border-red-300 focus:border-red-500"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDeleteSupplier}
              className="bg-red-600 hover:bg-red-700"
              disabled={!deleteSupplierReason || deleteSupplierReason.trim().length < 5}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* =========== SUPPLIER AUDIT DIALOG =========== */}
      <Dialog open={supplierAuditDialogOpen} onOpenChange={setSupplierAuditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Histórico - {selectedSupplier?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {supplierAuditLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sem registos de alteração</p>
            ) : (
              supplierAuditLogs.map((log) => (
                <div key={log.id} className="rounded-lg border bg-white p-3 shadow-sm" data-testid={`supplier-history-row-${log.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" className={cn(
                      log.action === 'created' && 'bg-green-50 text-green-700 border-green-300',
                      log.action === 'edited' && 'bg-amber-50 text-amber-700 border-amber-300',
                      log.action === 'deleted' && 'bg-red-50 text-red-700 border-red-300',
                      log.action === 'restored' && 'bg-blue-50 text-blue-700 border-blue-300'
                    )}>
                      {log.action === 'created' && 'Criação'}
                      {log.action === 'edited' && 'Edição'}
                      {log.action === 'deleted' && 'Eliminação'}
                      {log.action === 'restored' && 'Restauração'}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <User className="h-4 w-4 text-slate-500" />
                    <span>{log.user_name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {log.user_role === 'admin' ? 'Admin' : log.user_role === 'store_manager' ? 'Gerente' : log.user_role}
                    </Badge>
                  </div>
                  
                  {/* Changes */}
                  {log.changes && log.changes.length > 0 && (
                    <div className="bg-slate-50 rounded p-2 mb-2">
                      <p className="text-xs font-medium text-slate-600 mb-1">Alterações:</p>
                      {log.changes.map((change, i) => (
                        <div key={`${log.id}-${change.field || change.field_label || 'change'}-${i}`} className="flex items-center gap-2 text-xs">
                          <span className="font-medium">{change.field_label || change.field}:</span>
                          <span className="text-red-600 line-through">{String(change.old_value || '-')}</span>
                          <span>→</span>
                          <span className="text-green-600">{String(change.new_value || '-')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Justification */}
                  {(log.justification || log.delete_reason) && (
                    <div className="bg-amber-50 p-2 rounded border border-amber-200">
                      <p className="text-xs font-medium text-amber-700">
                        {log.action === 'deleted' ? 'Motivo:' : 'Justificação:'}
                      </p>
                      <p className="text-sm text-amber-800">&ldquo;{log.justification || log.delete_reason}&rdquo;</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== SUPPLIER LEDGER DIALOG =========== */}
      <Dialog open={supplierLedgerDialogOpen} onOpenChange={setSupplierLedgerDialogOpen}>
        <SupplierLedgerDialog
          ledger={selectedSupplierLedger}
          filteredTimeline={filteredSupplierTimeline}
          timelineFilters={timelineFilters}
          timelinePresets={TIMELINE_PRESETS}
          timelineStatusOptions={timelineStatusOptions}
          ledgerExporting={ledgerExporting}
          onExport={handleExportSuppliersLedger}
          onPresetChange={handleTimelinePresetChange}
          onEventTypeChange={(value) => setTimelineFilters((current) => ({ ...current, event_type: value }))}
          onStatusChange={(value) => setTimelineFilters((current) => ({ ...current, status_filter: value }))}
          onStartDateChange={(event) => setTimelineFilters((current) => ({ ...current, preset: 'custom', start_date: event.target.value }))}
          onEndDateChange={(event) => setTimelineFilters((current) => ({ ...current, preset: 'custom', end_date: event.target.value }))}
          formatCurrency={formatCurrency}
          formatDateTime={formatDateTime}
          statusColors={statusColors}
          statusLabels={statusLabels}
          paymentStatusColors={paymentStatusColors}
          paymentStatusLabels={paymentStatusLabels}
        />
      </Dialog>

      {/* =========== RECEIVE PO DIALOG =========== */}
      <Dialog open={receivePODialogOpen} onOpenChange={setReceivePODialogOpen}>
        <DialogContent className="w-[min(100vw-1rem,1480px)] max-w-[1480px] max-h-[94vh] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-xl" data-testid="procurement-receive-po-dialog-shell">
          <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
            <DialogTitle>Receber Ordem de Compra {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>

          <div className="max-h-[78vh] overflow-y-auto px-4 py-4 sm:px-6 xl:px-8 xl:py-6">
            {selectedPO && (
              <div className="space-y-5">
                {(() => {
                  const summary = getPOReceiptSummary(selectedPO);
                  const projectedReceived = receivePOItems.reduce((sum, item) => sum + Number(item.received_quantity || 0) + Number(item.receive_now || 0), 0);
                  const projectedPending = receivePOItems.reduce((sum, item) => sum + Math.max(Number(item.pending_quantity || 0) - Number(item.receive_now || 0), 0), 0);
                  return (
                    <div className="grid gap-3 md:grid-cols-4">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="po-receive-summary-ordered">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Encomendado</p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">{summary.ordered}</p>
                      </div>
                      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="po-receive-summary-received">
                        <p className="text-xs uppercase tracking-[0.18em] text-blue-700">Já recebido</p>
                        <p className="mt-1 text-lg font-semibold text-blue-900">{summary.received}</p>
                      </div>
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="po-receive-summary-pending">
                        <p className="text-xs uppercase tracking-[0.18em] text-amber-700">Pendente</p>
                        <p className="mt-1 text-lg font-semibold text-amber-900">{summary.pending}</p>
                      </div>
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="po-receive-summary-projected">
                        <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Após esta recepção</p>
                        <p className="mt-1 text-sm font-semibold text-emerald-900">{projectedReceived}/{summary.ordered} recebidos</p>
                        <p className="mt-1 text-xs text-emerald-700">{projectedPending} pendentes</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="rounded-2xl border border-slate-200 bg-white" data-testid="po-receive-items-table">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[920px] text-sm">
                      <thead className="border-b bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-slate-500">Produto</th>
                          <th className="px-4 py-3 text-right font-medium text-slate-500">Encomendado</th>
                          <th className="px-4 py-3 text-right font-medium text-slate-500">Recebido</th>
                          <th className="px-4 py-3 text-right font-medium text-slate-500">Pendente</th>
                          <th className="px-4 py-3 text-right font-medium text-slate-500">Receber agora</th>
                          <th className="px-4 py-3 text-right font-medium text-slate-500">Pendente após</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receivePOItems.map((item, index) => {
                          const pendingAfter = Math.max(Number(item.pending_quantity || 0) - Number(item.receive_now || 0), 0);
                          return (
                            <tr key={`${item.product_id}-${index}`} className="border-b last:border-0">
                              <td className="px-4 py-3 font-medium text-slate-800">{item.product_name}</td>
                              <td className="px-4 py-3 text-right">{item.ordered_quantity}</td>
                              <td className="px-4 py-3 text-right text-blue-700">{item.received_quantity}</td>
                              <td className="px-4 py-3 text-right text-amber-700">{item.pending_quantity}</td>
                              <td className="px-4 py-3 text-right">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  max={item.pending_quantity}
                                  value={item.receive_now}
                                  onChange={(event) => {
                                    const nextValue = Number(event.target.value || 0);
                                    setReceivePOItems((current) => current.map((row, rowIndex) => rowIndex === index ? {
                                      ...row,
                                      receive_now: Math.max(0, Math.min(nextValue, Number(row.pending_quantity || 0))),
                                    } : row));
                                  }}
                                  className="ml-auto w-28 text-right"
                                  data-testid={`po-receive-quantity-input-${index}`}
                                />
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-emerald-700" data-testid={`po-receive-pending-after-${index}`}>{pendingAfter}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="po-receive-notes">Notas de recepção</Label>
                  <Textarea
                    id="po-receive-notes"
                    value={receivePONotes}
                    onChange={(event) => setReceivePONotes(event.target.value)}
                    placeholder="Ex.: entrega parcial do fornecedor, conferência física, observações"
                    rows={3}
                    data-testid="po-receive-notes-input"
                  />
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={() => setReceivePODialogOpen(false)} data-testid="po-receive-cancel-button">Cancelar</Button>
                  <Button onClick={handleReceivePO} disabled={receivingPOId === selectedPO.id} data-testid="po-receive-confirm-button">
                    {receivingPOId === selectedPO.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                    Confirmar recepção
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== REGISTER SUPPLIER PAYMENT DIALOG =========== */}
      <Dialog open={paymentPODialogOpen} onOpenChange={setPaymentPODialogOpen}>
        <DialogContent className="w-[min(100vw-1rem,1180px)] max-w-[1180px] max-h-[92vh] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-xl" data-testid="procurement-pay-po-dialog-shell">
          <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
            <DialogTitle>Registar Pagamento ao Fornecedor · {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>

          <div className="max-h-[76vh] overflow-y-auto px-4 py-4 sm:px-6 xl:px-8 xl:py-6">
            {selectedPO && (
              <div className="space-y-5">
                {(() => {
                  const receipt = getPOReceiptSummary(selectedPO);
                  const financial = getPOFinancialSummary(selectedPO);
                  return (
                    <div className="grid gap-3 md:grid-cols-4">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid="po-payment-summary-ordered">
                        <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Encomendado</p>
                        <p className="mt-1 text-sm font-semibold text-slate-900">{formatCurrency(selectedPO.total_with_vat || 0)}</p>
                      </div>
                      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="po-payment-summary-payable">
                        <p className="text-xs uppercase tracking-[0.18em] text-blue-700">Apto para pagamento</p>
                        <p className="mt-1 text-sm font-semibold text-blue-900">{formatCurrency(financial.payable)}</p>
                        <p className="mt-1 text-xs text-blue-700">Baseado no recebido: {receipt.received}/{receipt.ordered}</p>
                      </div>
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="po-payment-summary-paid">
                        <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Pago ao fornecedor</p>
                        <p className="mt-1 text-sm font-semibold text-emerald-900">{formatCurrency(financial.paid)}</p>
                      </div>
                      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="po-payment-summary-balance">
                        <p className="text-xs uppercase tracking-[0.18em] text-amber-700">Saldo em aberto</p>
                        <p className="mt-1 text-sm font-semibold text-amber-900">{formatCurrency(financial.balance)}</p>
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-3 rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4" data-testid="po-payment-splits-editor">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Composição do pagamento</p>
                      <p className="text-xs text-slate-500">Forma de pagamento ≠ conta de pagamento. Pode compor o registo em várias linhas.</p>
                    </div>
                    <Button variant="outline" size="sm" type="button" onClick={addPaymentSplitLine} data-testid="po-payment-add-split-line-button">
                      <Plus className="mr-2 h-4 w-4" /> Adicionar linha
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {(paymentPOForm.payments || []).map((line, index) => {
                      const availableAccounts = paymentAccounts.filter((account) => !line.payment_method_id || account.payment_method_id === line.payment_method_id);
                      return (
                        <div key={`payment-line-${index}`} className="grid gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 md:grid-cols-[1.2fr_1.2fr_0.8fr_1fr_auto]" data-testid={`po-payment-split-line-${index}`}>
                          <div className="space-y-2">
                            <Label>Forma de pagamento</Label>
                            <Select value={line.payment_method_id} onValueChange={(value) => updatePaymentSplitLine(index, 'payment_method_id', value)}>
                              <SelectTrigger data-testid={`po-payment-method-select-${index}`}>
                                <SelectValue placeholder="Seleccionar forma" />
                              </SelectTrigger>
                              <SelectContent>
                                {paymentMethods.map((method) => (
                                  <SelectItem key={method.id} value={method.id}>{method.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Conta de pagamento</Label>
                            <Select value={line.payment_account_id} onValueChange={(value) => updatePaymentSplitLine(index, 'payment_account_id', value)}>
                              <SelectTrigger data-testid={`po-payment-account-select-${index}`}>
                                <SelectValue placeholder="Opcional: conta" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableAccounts.map((account) => (
                                  <SelectItem key={account.id} value={account.id}>{account.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Valor</Label>
                            <Input type="number" min="0" step="0.01" value={line.amount} onChange={(event) => updatePaymentSplitLine(index, 'amount', event.target.value)} data-testid={`po-payment-amount-input-${index}`} />
                          </div>
                          <div className="space-y-2">
                            <Label>Referência</Label>
                            <Input value={line.reference || ''} onChange={(event) => updatePaymentSplitLine(index, 'reference', event.target.value)} data-testid={`po-payment-reference-input-${index}`} />
                          </div>
                          <div className="flex items-end justify-end">
                            <Button variant="ghost" size="icon" type="button" onClick={() => removePaymentSplitLine(index)} disabled={(paymentPOForm.payments || []).length === 1} data-testid={`po-payment-remove-line-${index}`}>
                              <Trash2 className="h-4 w-4 text-rose-600" />
                            </Button>
                          </div>
                          <div className="space-y-2 md:col-span-5">
                            <Label>Notas da linha</Label>
                            <Textarea value={line.notes || ''} onChange={(event) => updatePaymentSplitLine(index, 'notes', event.target.value)} rows={2} data-testid={`po-payment-notes-input-${index}`} />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-2">
                    <Label>Estado financeiro previsto</Label>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="po-payment-projected-status">
                      {(() => {
                        const financial = getPOFinancialSummary(selectedPO);
                        const totalPlanned = (paymentPOForm.payments || []).reduce((sum, line) => sum + Number(line.amount || 0), 0);
                        const nextPaid = financial.paid + totalPlanned;
                        const nextBalance = Math.max(financial.payable - nextPaid, 0);
                        return `Pago após registo: ${formatCurrency(nextPaid)} · Saldo após registo: ${formatCurrency(nextBalance)}`;
                      })()}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={() => setPaymentPODialogOpen(false)} data-testid="po-payment-cancel-button">Cancelar</Button>
                  <Button onClick={handleRegisterPOPayment} disabled={registeringPaymentPOId === selectedPO.id} data-testid="po-payment-confirm-button">
                    {registeringPaymentPOId === selectedPO.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRightLeft className="mr-2 h-4 w-4" />}
                    Registar pagamento
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== CREATE PO DIALOG =========== */}
      <Dialog open={poDialogOpen} onOpenChange={(open) => { if (!open) resetPOBuilder(); setPODialogOpen(open); }}>
        <DialogContent className="w-[min(100vw-1rem,1560px)] max-w-[1560px] max-h-[94vh] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-xl" data-testid="procurement-create-po-dialog-shell">
          <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
            <DialogTitle>Nova Ordem de Compra</DialogTitle>
          </DialogHeader>
          <div className="max-h-[78vh] overflow-y-auto px-4 py-4 sm:px-6 xl:px-8 xl:py-6">
          <div className="grid gap-6 xl:grid-cols-[minmax(420px,0.95fr)_minmax(0,1.05fr)]">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Fornecedor *</h3>
                <Select value={selectedSupplierForPO} onValueChange={setSelectedSupplierForPO}>
                  <SelectTrigger data-testid="po-supplier-select">
                    <SelectValue placeholder="Seleccionar fornecedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeSuppliers.map(supplier => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} ({supplier.code || supplier.supplier_code || 'N/A'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3" data-testid="po-supplier-fiscal-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Perfil fiscal do fornecedor</p>
                    <p className="text-xs text-slate-500">Um único fornecedor por PO. Esta configuração controla o IVA e a emissão fiscal desta ordem.</p>
                  </div>
                  {selectedSupplierDetails && (
                    <Badge variant="outline" className="rounded-full border-slate-300 bg-white text-slate-700" data-testid="po-selected-supplier-badge">
                      {selectedSupplierDetails.name}
                    </Badge>
                  )}
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Fornecedor cobra IVA?</Label>
                    <Select value={poSupplierChargesVat ? 'vat' : 'no_vat'} onValueChange={(value) => setPOSupplierChargesVat(value === 'vat')}>
                      <SelectTrigger data-testid="po-supplier-vat-mode-select">
                        <SelectValue placeholder="Seleccionar regime IVA" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vat">Sim, cobra IVA</SelectItem>
                        <SelectItem value="no_vat">Não cobra IVA</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Emite documento fiscal?</Label>
                    <Select value={poSupplierIssuesFiscalDocument ? 'formal' : 'informal'} onValueChange={(value) => setPOSupplierIssuesFiscalDocument(value === 'formal')}>
                      <SelectTrigger data-testid="po-supplier-fiscal-document-select">
                        <SelectValue placeholder="Seleccionar emissão fiscal" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="formal">Sim, emite documento fiscal</SelectItem>
                        <SelectItem value="informal">Não emite documento fiscal formal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-2">Adicionar Items</h3>
                <div className="space-y-3">
                  <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar produto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} ({product.sku})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value))}
                      placeholder="Quantidade"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={unitCost}
                      onChange={(e) => setUnitCost(e.target.value)}
                      placeholder="Custo Unitário (MT)"
                    />
                  </div>
                  <Button onClick={addItemToPO} className="w-full" type="button">
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar à PO
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expected_delivery">Entrega Prevista</Label>
                <Input
                  id="expected_delivery"
                  type="date"
                  value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="po_notes">Notas</Label>
                <Textarea
                  id="po_notes"
                  value={poNotes}
                  onChange={(e) => setPONotes(e.target.value)}
                  placeholder="Notas opcionais"
                  rows={2}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium">Items da Ordem</h3>
              <div className="border rounded-md p-4 space-y-3 min-h-[240px]">
                {poItems.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">Sem items adicionados</p>
                ) : (
                  poItems.map((item, index) => (
                    <div key={item.id || item.product_id || `${item.product_name || 'po-item'}-${index}`} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground">{item.quantity} x {formatCurrency(item.unit_cost)} · fornecedor {item.supplier_charges_vat === false ? 'sem IVA' : 'com IVA'}</p>
                        <p className={cn('text-xs', item.supplier_charges_vat === false ? 'text-slate-500' : 'text-blue-700')}>
                          IVA {getItemVatRate(item).toFixed(0)}%: {formatCurrency(getItemVatAmount(item))} • Com IVA: {formatCurrency((item.quantity || 0) * getItemUnitCostWithVat(item))}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{formatCurrency(item.quantity * item.unit_cost)}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeItemFromPO(index)}
                          type="button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span>Subtotal (sem IVA):</span>
                  <span>{formatCurrency(calculatePOTotal())}</span>
                </div>
                <div className="flex justify-between text-sm text-blue-700">
                  <span>IVA Total ({poSupplierChargesVat ? 'fornecedor com IVA' : 'fornecedor sem IVA'}):</span>
                  <span>{formatCurrency(calculatePOVATTotal())}</span>
                </div>
                <div className="mt-1 flex justify-between text-lg font-bold">
                  <span>Total com IVA:</span>
                  <span>{formatCurrency(calculatePOGrandTotal())}</span>
                </div>
              </div>
              <Button
                onClick={handleCreatePO}
                className="w-full"
                disabled={poItems.length === 0 || !selectedSupplierForPO}
                type="button"
                data-testid="create-po-button"
              >
                Criar Ordem de Compra
              </Button>
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== VIEW PO DIALOG =========== */}
      <Dialog open={viewPODialogOpen} onOpenChange={setViewPODialogOpen}>
        <DialogContent className="w-[min(100vw-1rem,1560px)] max-w-[1560px] max-h-[94vh] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-xl" data-testid="po-view-dialog-shell">
          <DialogHeader className="border-b border-slate-200 bg-slate-50 px-4 py-4 sm:px-6">
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              Ordem de Compra - {selectedPO?.po_number}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Visualização operacional da ordem de compra com recepção, histórico de stock e situação financeira do fornecedor.
            </DialogDescription>
          </DialogHeader>
          
          {selectedPO && (
            <div className="max-h-[78vh] overflow-y-auto px-4 py-4 sm:px-6 xl:px-8 xl:py-6">
            <div className="space-y-4">
              {(() => {
                const riskLevel = getFiscalRiskLevel(selectedPO);
                const risk = fiscalRiskConfig[riskLevel] || fiscalRiskConfig.baixo;
                return (
                  <div className="flex items-center justify-between border rounded-lg p-3 bg-slate-50" data-testid="po-view-fiscal-risk-container">
                    <p className="text-sm text-muted-foreground">Risco Fiscal (informativo)</p>
                    <Badge className={risk.className} variant="outline" data-testid="po-view-fiscal-risk-badge">
                      {risk.label}
                    </Badge>
                  </div>
                );
              })()}

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Fornecedor</p>
                  <p className="font-medium" data-testid="po-view-supplier-name">{selectedPO.supplier_name}</p>
                  <p className="text-xs text-muted-foreground" data-testid="po-view-supplier-tax-id">
                    NUIT: {selectedPO.supplier_snapshot?.tax_id || 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="po-view-supplier-address">
                    Morada: {selectedPO.supplier_snapshot?.address || 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="po-view-supplier-city">
                    Cidade: {selectedPO.supplier_snapshot?.city || 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="po-view-supplier-contact-person">
                    Contacto: {selectedPO.supplier_snapshot?.contact_person || 'N/A'}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="po-view-supplier-vat-mode">
                    IVA: {selectedPO.supplier_charges_vat === false ? 'Fornecedor sem IVA' : 'Fornecedor cobra IVA'}
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="po-view-supplier-fiscal-document-mode">
                    Documento fiscal: {selectedPO.supplier_issues_fiscal_document === false ? 'Fornecedor informal / sem documento fiscal formal' : 'Fornecedor emite documento fiscal'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge className={statusColors[selectedPO.status]} variant="outline">
                    {statusLabels[selectedPO.status] || selectedPO.status}
                  </Badge>
                  <p className="mt-2 text-xs text-slate-500" data-testid="po-view-status-help">
                    {selectedPO.status === 'closed' ? 'PO fechada: totalmente recebida e bloqueada para novas recepções.' : 'Acompanhe recepção, pendência e impacto em stock nesta mesma ordem.'}
                  </p>
                </div>
              </div>

              {(() => {
                const receipt = getPOReceiptSummary(selectedPO);
                const financial = getPOFinancialSummary(selectedPO);
                return (
                  <div className="grid gap-3 md:grid-cols-4" data-testid="po-view-receipt-summary-grid">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Encomendado</p>
                      <p className="mt-1 text-lg font-semibold text-slate-900">{receipt.ordered}</p>
                      <p className="mt-1 text-xs text-slate-500">Valor encomenda: {formatCurrency(selectedPO.total_with_vat || 0)}</p>
                    </div>
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-blue-700">Recebido</p>
                      <p className="mt-1 text-lg font-semibold text-blue-900">{receipt.received}</p>
                    </div>
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-amber-700">Pendente</p>
                      <p className="mt-1 text-lg font-semibold text-amber-900">{receipt.pending}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Impacto em stock</p>
                      <p className="mt-1 text-lg font-semibold text-emerald-900">{receipt.received}</p>
                      <p className="mt-1 text-xs text-emerald-700">{receipt.completion}% da ordem aceite</p>
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const financial = getPOFinancialSummary(selectedPO);
                return (
                  <div className="grid gap-3 md:grid-cols-4" data-testid="po-view-financial-summary-grid">
                    <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-blue-700">Apto para pagamento</p>
                      <p className="mt-1 text-lg font-semibold text-blue-900">{formatCurrency(financial.payable)}</p>
                    </div>
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Pago ao fornecedor</p>
                      <p className="mt-1 text-lg font-semibold text-emerald-900">{formatCurrency(financial.paid)}</p>
                    </div>
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-amber-700">Saldo em aberto</p>
                      <p className="mt-1 text-lg font-semibold text-amber-900">{formatCurrency(financial.balance)}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Estado financeiro</p>
                      <div className="mt-1"><Badge variant="outline" className={paymentStatusColors[financial.paymentStatus]}>{paymentStatusLabels[financial.paymentStatus]}</Badge></div>
                    </div>
                  </div>
                );
              })()}
              
              <div className="grid gap-4 text-sm md:grid-cols-4">
                <div>
                  <p className="text-muted-foreground">Criada em</p>
                  <p>{formatDateTime(selectedPO.created_at)}</p>
                </div>
                {selectedPO.approved_at && (
                  <div>
                    <p className="text-muted-foreground">Aprovada em</p>
                    <p>{formatDateTime(selectedPO.approved_at)}</p>
                  </div>
                )}
                {selectedPO.received_at && (
                  <div>
                    <p className="text-muted-foreground">Recebida em</p>
                    <p>{formatDateTime(selectedPO.received_at)}</p>
                  </div>
                )}
                {selectedPO.closed_at && (
                  <div>
                    <p className="text-muted-foreground">Fechada em</p>
                    <p>{formatDateTime(selectedPO.closed_at)}</p>
                  </div>
                )}
              </div>

              {getSupplierMissingWarnings(selectedPO).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="po-view-supplier-warning-banner">
                  <p className="text-sm font-medium text-amber-800">Aviso: dados fiscais do fornecedor incompletos</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Campos em falta: {getSupplierMissingWarnings(selectedPO).join(', ')}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => handlePrintPO(selectedPO, 'Original')}
                  disabled={!!printingPO}
                  data-testid="po-view-print-original-button"
                >
                  {printingPO === `${selectedPO.id}-Original` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                  Imprimir Original
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePrintPO(selectedPO, 'Duplicado')}
                  disabled={!!printingPO}
                  data-testid="po-view-print-duplicate-button"
                >
                  {printingPO === `${selectedPO.id}-Duplicado` ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
                  Imprimir Duplicado
                </Button>
                {['approved', 'partially_received'].includes(selectedPO.status) && canEdit && (
                  <Button variant="outline" onClick={() => handleOpenReceivePO(selectedPO)} data-testid="po-view-receive-button">
                    <Check className="mr-2 h-4 w-4" /> Registar recepção
                  </Button>
                )}
                {selectedPO.status === 'received' && canEdit && (
                  <Button variant="outline" onClick={() => handleClosePO(selectedPO.id)} disabled={closingPOId === selectedPO.id} data-testid="po-view-close-button">
                    {closingPOId === selectedPO.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />} Fechar PO
                  </Button>
                )}
                {selectedPO.eligible_for_payment && Number(selectedPO.supplier_balance_due || 0) > 0 && canEdit && (
                  <Button variant="outline" onClick={() => handleOpenPaymentPO(selectedPO)} data-testid="po-view-pay-button">
                    <ArrowRightLeft className="mr-2 h-4 w-4" /> Registar pagamento fornecedor
                  </Button>
                )}
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3">Items ({selectedPO.items.length})</h4>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2">Produto</th>
                      <th className="text-right py-2">Encomendado</th>
                      <th className="text-right py-2">Recebido</th>
                      <th className="text-right py-2">Pendente</th>
                      <th className="text-right py-2">Sem IVA</th>
                      <th className="text-right py-2">IVA</th>
                      <th className="text-right py-2">Com IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items.map((item, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2">{item.product_name}</td>
                        <td className="text-right py-2">{item.quantity}</td>
                        <td className="text-right py-2 text-blue-700">{item.received_quantity || 0}</td>
                        <td className="text-right py-2 text-amber-700">{item.pending_quantity ?? Math.max((item.quantity || 0) - (item.received_quantity || 0), 0)}</td>
                        <td className="text-right py-2">{formatCurrency(item.quantity * item.unit_cost)}</td>
                        <td className="text-right py-2 text-blue-700">{formatCurrency(getItemVatAmount(item))}</td>
                        <td className="text-right py-2 font-medium">{formatCurrency((item.quantity || 0) * getItemUnitCostWithVat(item))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t">
                    <tr>
                      <td colSpan={6} className="py-2 font-bold text-right">Subtotal (sem IVA):</td>
                      <td className="py-2 font-bold text-right">{formatCurrency(selectedPO.total_amount)}</td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="py-2 font-bold text-right text-blue-700">IVA Total:</td>
                      <td className="py-2 font-bold text-right text-blue-700">{formatCurrency(selectedPO.vat_total || 0)}</td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="py-2 font-bold text-right">Total com IVA:</td>
                      <td className="py-2 font-bold text-right">{formatCurrency(selectedPO.total_with_vat || ((selectedPO.total_amount || 0) + (selectedPO.vat_total || 0)))}</td>
                    </tr>
                  </tfoot>
                </table>
                </div>
              </div>

              {!!selectedPO.received_items?.length && (
                <div className="border rounded-lg p-4" data-testid="po-view-receipt-history-card">
                  <h4 className="font-medium mb-3">Histórico de recepções ({selectedPO.received_items.length})</h4>
                  <div className="space-y-3">
                    {selectedPO.received_items.map((receipt, index) => (
                      <div key={receipt.receipt_id || index} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`po-view-receipt-history-${index}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-slate-800">Recepção {index + 1}</p>
                            <p className="text-xs text-slate-500">{formatDateTime(receipt.received_at)} · {receipt.received_by_name || 'Sistema'}</p>
                          </div>
                          <Badge variant="outline" className="rounded-full">{(receipt.items || []).length} item(ns)</Badge>
                        </div>
                        <div className="mt-3 space-y-1 text-sm text-slate-700">
                          {(receipt.items || []).map((line, lineIndex) => (
                            <div key={`${receipt.receipt_id || index}-${lineIndex}`} className="flex flex-wrap justify-between gap-2">
                              <span>{line.product_name}</span>
                              <span className="text-right">Recebido: {line.received_quantity} · Pendente: {line.pending_quantity_after}</span>
                            </div>
                          ))}
                        </div>
                        {receipt.notes && <p className="mt-2 text-xs text-slate-500">Nota: {receipt.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!!selectedPO.payments?.length && (
                <div className="border rounded-lg p-4" data-testid="po-view-payment-history-card">
                  <h4 className="font-medium mb-3">Histórico de pagamentos ao fornecedor ({selectedPO.payments.length})</h4>
                  <div className="space-y-3">
                    {selectedPO.payments.map((payment, index) => (
                      <div key={payment.id || index} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`po-view-payment-history-${index}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{formatCurrency(payment.amount)} · {payment.payment_method_name || 'Forma de pagamento'}</p>
                            <p className="text-xs text-slate-500">Conta: {payment.payment_account_name || 'Sem conta associada'}</p>
                            <p className="text-xs text-slate-500">{formatDateTime(payment.paid_at)} · {payment.created_by_name || 'Sistema'}</p>
                          </div>
                          {payment.reference && <Badge variant="outline" className="rounded-full">{payment.reference}</Badge>}
                        </div>
                        {payment.notes && <p className="mt-2 text-xs text-slate-500">Nota: {payment.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedPO.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notas</p>
                  <p className="text-sm">{selectedPO.notes}</p>
                </div>
              )}
            </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* =========== EDIT PO DIALOG =========== */}
      <Dialog open={editPODialogOpen} onOpenChange={setEditPODialogOpen}>
        <DialogContent className="w-[min(100vw-1rem,1560px)] max-w-[1560px] max-h-[94vh] overflow-hidden rounded-[28px] border border-slate-200 bg-white p-0 shadow-xl" data-testid="procurement-edit-po-dialog-shell">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              Editar PO - {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[78vh] overflow-y-auto px-4 py-4 sm:px-6 xl:px-8 xl:py-6 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Edição Auditada</p>
                  <p className="text-sm text-amber-700">
                    Apenas ordens em rascunho podem ser editadas. Alterações ficam registadas no histórico.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid gap-6 xl:grid-cols-[minmax(420px,0.95fr)_minmax(0,1.05fr)]">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Fornecedor único da PO *</Label>
                  <Select value={editPOSupplierId} onValueChange={setEditPOSupplierId}>
                    <SelectTrigger data-testid="edit-po-supplier-select">
                      <SelectValue placeholder="Seleccionar fornecedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeSuppliers.map((supplier) => (
                        <SelectItem key={supplier.id} value={supplier.id}>{supplier.name} ({supplier.code || supplier.supplier_code || 'N/A'})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3" data-testid="edit-po-supplier-fiscal-panel">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Perfil fiscal desta PO</p>
                    <p className="text-xs text-slate-500">Sem assumir IVA automaticamente. Fornecedor sem documento fiscal formal é aceite.</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Fornecedor cobra IVA?</Label>
                      <Select value={editPOSupplierChargesVat ? 'vat' : 'no_vat'} onValueChange={(value) => setEditPOSupplierChargesVat(value === 'vat')}>
                        <SelectTrigger data-testid="edit-po-supplier-vat-mode-select">
                          <SelectValue placeholder="Seleccionar regime IVA" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vat">Sim, cobra IVA</SelectItem>
                          <SelectItem value="no_vat">Não cobra IVA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Emite documento fiscal?</Label>
                      <Select value={editPOSupplierIssuesFiscalDocument ? 'formal' : 'informal'} onValueChange={(value) => setEditPOSupplierIssuesFiscalDocument(value === 'formal')}>
                        <SelectTrigger data-testid="edit-po-supplier-fiscal-document-select">
                          <SelectValue placeholder="Seleccionar emissão fiscal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="formal">Sim, emite documento fiscal</SelectItem>
                          <SelectItem value="informal">Não emite documento fiscal formal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Entrega Prevista</Label>
                  <Input
                    type="date"
                    value={editPODelivery}
                    onChange={(e) => setEditPODelivery(e.target.value)}
                    data-testid="edit-po-delivery-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Textarea
                    value={editPONotes}
                    onChange={(e) => setEditPONotes(e.target.value)}
                    rows={3}
                    data-testid="edit-po-notes-input"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-4 space-y-3" data-testid="edit-po-items-panel">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Linhas da ordem</p>
                      <p className="text-xs text-slate-500">Todas as quantidades e custos ficam editáveis enquanto a PO estiver em rascunho.</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {editPOItems.map((item, index) => (
                      <div key={`edit-po-item-${item.product_id}-${index}`} className="grid gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 md:grid-cols-[1.6fr_0.7fr_0.9fr_auto]" data-testid={`edit-po-item-row-${index}`}>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{item.product_name}</p>
                          <p className="text-xs text-slate-500">IVA {editPOSupplierChargesVat ? `${Number(item.vat_rate || 16).toFixed(0)}%` : '0%'} · fornecedor {editPOSupplierChargesVat ? 'com IVA' : 'sem IVA'}</p>
                        </div>
                        <Input type="number" min="1" value={item.quantity} onChange={(event) => setEditPOItems((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, quantity: Number(event.target.value || 0), supplier_charges_vat: editPOSupplierChargesVat, vat_rate: editPOSupplierChargesVat ? Number(row.vat_rate || 16) : 0 } : row))} data-testid={`edit-po-item-quantity-input-${index}`} />
                        <Input type="number" min="0" step="0.01" value={item.unit_cost} onChange={(event) => setEditPOItems((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, unit_cost: Number(event.target.value || 0), supplier_charges_vat: editPOSupplierChargesVat, vat_rate: editPOSupplierChargesVat ? Number(row.vat_rate || 16) : 0 } : row))} data-testid={`edit-po-item-unit-cost-input-${index}`} />
                        <Button variant="ghost" size="icon" type="button" onClick={() => setEditPOItems((current) => current.filter((_, rowIndex) => rowIndex !== index))} data-testid={`edit-po-item-remove-button-${index}`}>
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="grid gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <div className="flex items-center justify-between"><span>Subtotal</span><span>{formatCurrency(editPOItems.reduce((sum, item) => sum + (Number(item.quantity || 0) * Number(item.unit_cost || 0)), 0))}</span></div>
                    <div className="flex items-center justify-between text-blue-700"><span>IVA</span><span>{formatCurrency(editPOItems.reduce((sum, item) => sum + ((Number(item.quantity || 0) * Number(item.unit_cost || 0) * ((editPOSupplierChargesVat ? Number(item.vat_rate || 16) : 0) / 100))), 0))}</span></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 border-t pt-4">
              <Label className="text-amber-700 font-medium">Justificação da Alteração *</Label>
              <Textarea
                value={editPOJustification}
                onChange={(e) => setEditPOJustification(e.target.value)}
                placeholder="Descreva o motivo desta alteração (obrigatório)..."
                rows={2}
                className="border-amber-300 focus:border-amber-500"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditPODialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveEditPO}
                disabled={!editPOJustification || editPOJustification.trim().length < 5 || !editPOSupplierId || !editPOItems.length}
                data-testid="edit-po-save-button"
              >
                Guardar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== CONFIRM EDIT PO DIALOG =========== */}
      <AlertDialog open={confirmEditPODialogOpen} onOpenChange={setConfirmEditPODialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Alteração</AlertDialogTitle>
            <AlertDialogDescription>
              <p>Esta alteração ficará registada no histórico de auditoria.</p>
              <p className="mt-2"><strong>Justificação:</strong> &ldquo;{editPOJustification}&rdquo;</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEditPO}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* =========== CANCEL PO DIALOG =========== */}
      <AlertDialog open={cancelPODialogOpen} onOpenChange={setCancelPODialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Cancelar Ordem de Compra
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Tem certeza que deseja cancelar a ordem de compra <strong>{selectedPO?.po_number}</strong>?
              </p>
              <p className="text-sm">
                Fornecedor: {selectedPO?.supplier_name} | Valor: {formatCurrency(selectedPO?.total_amount || 0)}
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  Esta acção não pode ser desfeita. O cancelamento ficará registado no histórico.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-red-700 font-medium">Motivo do Cancelamento *</Label>
                <Textarea
                  value={cancelPOReason}
                  onChange={(e) => setCancelPOReason(e.target.value)}
                  placeholder="Descreva o motivo do cancelamento (obrigatório)..."
                  rows={2}
                  className="border-red-300 focus:border-red-500"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmCancelPO}
              className="bg-red-600 hover:bg-red-700"
              disabled={!cancelPOReason || cancelPOReason.trim().length < 5}
            >
              Cancelar PO
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* =========== PO AUDIT DIALOG =========== */}
      <Dialog open={poAuditDialogOpen} onOpenChange={setPOAuditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Histórico - {selectedPO?.po_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {poAuditLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Sem registos de alteração</p>
            ) : (
              poAuditLogs.map((log) => (
                <div key={log.id} className="rounded-lg border bg-white p-3 shadow-sm" data-testid={`po-history-row-${log.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" className={cn(
                      log.action === 'created' && 'bg-green-50 text-green-700 border-green-300',
                      log.action === 'edited' && 'bg-amber-50 text-amber-700 border-amber-300',
                      log.action === 'approved' && 'bg-blue-50 text-blue-700 border-blue-300',
                      log.action === 'received' && 'bg-green-50 text-green-700 border-green-300',
                      log.action === 'cancelled' && 'bg-red-50 text-red-700 border-red-300'
                    )}>
                      {log.action === 'created' && 'Criação'}
                      {log.action === 'edited' && 'Edição'}
                      {log.action === 'approved' && 'Aprovação'}
                      {log.action === 'received' && 'Recepção'}
                      {log.action === 'cancelled' && 'Cancelamento'}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <User className="h-4 w-4 text-slate-500" />
                    <span>{log.user_name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {log.user_role === 'admin' ? 'Admin' : log.user_role === 'store_manager' ? 'Gerente' : log.user_role}
                    </Badge>
                  </div>
                  
                  {/* Details */}
                  {log.details && (
                    <p className="text-sm text-slate-600 mb-2">{log.details}</p>
                  )}
                  
                  {/* Changes */}
                  {log.changes && log.changes.length > 0 && (
                    <div className="bg-slate-50 rounded p-2 mb-2">
                      <p className="text-xs font-medium text-slate-600 mb-1">Alterações:</p>
                      {log.changes.map((change, i) => (
                        <div key={`${log.id}-${change.field || change.field_label || 'change'}-${i}`} className="flex items-center gap-2 text-xs">
                          <span className="font-medium">{change.field_label || change.field}:</span>
                          <span className="text-red-600 line-through">{String(change.old_value || '-')}</span>
                          <span>→</span>
                          <span className="text-green-600">{String(change.new_value || '-')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Justification / Cancel Reason */}
                  {(log.justification || log.cancel_reason) && (
                    <div className={cn(
                      "p-2 rounded border",
                      log.cancel_reason ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"
                    )}>
                      <p className={cn(
                        "text-xs font-medium",
                        log.cancel_reason ? "text-red-700" : "text-amber-700"
                      )}>
                        {log.cancel_reason ? 'Motivo:' : 'Justificação:'}
                      </p>
                      <p className={cn(
                        "text-sm",
                        log.cancel_reason ? "text-red-800" : "text-amber-800"
                      )}>&ldquo;{log.justification || log.cancel_reason}&rdquo;</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MissingMasterDataDialog
        open={poPrintValidationState.open}
        onOpenChange={(open) => {
          if (!open) {
            setPOPrintValidationState({ open: false, warnings: [], blob: null, filename: '', po: null, copyType: 'Original' });
          }
        }}
        warnings={poPrintValidationState.warnings}
        entityName={poPrintValidationState.po?.supplier_name}
        entityTypeLabel="Fornecedor"
        editEntityLabel="Editar Fornecedor"
        onEditEntity={handleEditSupplierFromPOPrint}
        onPrintAnyway={handlePrintPOWithMissingData}
      />
    </div>
  );
}
